import React, { useState, useEffect, useCallback, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { useLocation, Navigate, useNavigate } from 'react-router-dom';
import BookAppointmentFlow from './BookAppointmentFlow';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  AreaChart, Area,
  ResponsiveContainer,
} from 'recharts';
import DataTable from 'react-data-table-component';
// Video data now fetched from API instead of static file
import api from './api';
import InstallPrompt from './InstallPrompt';
import './SaadhakDashboard.css';

import bgProfile from '../assets/images/bg-profile.jpeg';
import logo      from '../assets/images/logo-ct.png';

// ── Chart palette ────────────────────────────────────────────────────────────
const CHART_COLORS = ['#C9A84C', '#4ADE80', '#60A5FA', '#F472B6', '#A78BFA', '#FB923C'];

// ── Progress Ring (SVG — iOS 12 safe) ────────────────────────────────────────
const ProgressRing = ({ value, maxValue, size, strokeWidth, color, centerLabel, centerSub }) => {
  const pct    = maxValue > 0 ? Math.min((value / maxValue) * 100, 100) : 0;
  const r      = (size - strokeWidth) / 2;
  const circ   = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <div className="sd-ring-wrap">
      <svg width={size} height={size} style={{ display: 'block', WebkitTransform: 'rotate(-90deg)', transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={strokeWidth} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circ + ' ' + circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="sd-ring-center">
        <span className="sd-ring-val">{centerLabel}</span>
        <span className="sd-ring-sub">{centerSub}</span>
      </div>
    </div>
  );
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const getYoutubeId = (url) => {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) return u.pathname.slice(1);
    return u.searchParams.get('v');
  } catch (e) { return null; }
};

// ── Reels-style Feed Card (full viewport height, auto-play on scroll) ────────
// isNearby = within buffer zone (±2 of active), pre-loads Drive videos (muted)
// isActive = center of screen, mounts YouTube iframe (autoplay=1), plays Drive
function FeedCard({ video, isActive, isNearby, label, progress, locked, onTap }) {
  var isDrive = video.source === 'drive';
  var isShort = video.videoType === 'short';
  var _e = useState(false), hasError = _e[0], setHasError = _e[1];
  var videoRef = useRef(null);
  var iframeRef = useRef(null);

  var thumbUrl = !isDrive && video.youtubeId
    ? 'https://img.youtube.com/vi/' + video.youtubeId + '/hqdefault.jpg' : '';

  var shouldMountDrive = !locked && (isActive || isNearby);
  // YouTube iframes: ONLY mount when active to prevent echo.
  // Unmounting removes the iframe from DOM — browser stops playback instantly.
  var shouldMountYT = !locked && isActive;

  var iframeSrc = !isDrive && shouldMountYT
    ? ('https://www.youtube.com/embed/' + video.youtubeId
      + '?rel=0&modestbranding=1&controls=1&playsinline=1&enablejsapi=1&autoplay=1')
    : '';

  var iframeLoaded = useRef(false);

  // Reset iframeLoaded when YouTube iframe unmounts
  useEffect(function() {
    if (!shouldMountYT) iframeLoaded.current = false;
  }, [shouldMountYT]);

  useEffect(function() {
    if (locked || !isDrive) return;
    var v = videoRef.current;
    if (!v) return;
    if (isActive) { try { v.play(); } catch (_) {} }
    else { try { v.pause(); } catch (_) {} }
  }, [isActive, isDrive, locked]);

  function handleIframeLoad() {
    iframeLoaded.current = true;
  }

  var driveStreamUrl = isDrive
    ? process.env.REACT_APP_URL + '/resource-videos/stream/' + video._id : '';

  var aspectPad = '150%'; // 2:3 tall portrait — nearly full mobile screen like Instagram Reels
  var pct = progress ? progress.percentWatched : 0;
  var meta = label ? (LABEL_META[label] || {}) : {};

  return (
    <div onClick={function() { if (!locked && onTap) onTap(video); }}
      style={{
        position: 'relative', width: '100%', background: '#000',
        borderRadius: 16, overflow: 'hidden', marginBottom: 14,
        boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
        cursor: locked ? 'default' : 'pointer',
        opacity: locked ? 0.5 : 1,
      }}>
      <div style={{ paddingTop: aspectPad, position: 'relative' }}>
        {isDrive ? (
          shouldMountDrive ? (
            <video
              ref={videoRef}
              src={driveStreamUrl}
              controls controlsList="nodownload"
              onContextMenu={function(e) { e.preventDefault(); }}
              playsInline muted
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <div style={{
              position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#111', color: '#666',
            }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <polygon points="10,8 16,12 10,16" fill="currentColor"/>
                <rect x="2" y="3" width="20" height="18" rx="3"/>
              </svg>
            </div>
          )
        ) : shouldMountYT && !hasError ? (
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            title={video.title}
            referrerPolicy="strict-origin-when-cross-origin"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            onLoad={handleIframeLoad}
            onError={function() { setHasError(true); }}
          />
        ) : hasError ? (
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#111', color: '#aaa' }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>!</div>
            <div style={{ fontSize: 12 }}>Video unavailable</div>
            <a href={'https://www.youtube.com/watch?v=' + video.youtubeId + '&ab_channel=MedicalYogaCentre'} target="_blank" rel="noreferrer"
              style={{ marginTop: 8, fontSize: 11, color: '#C9A84C', textDecoration: 'underline' }}>
              Watch on YouTube
            </a>
          </div>
        ) : (
          <div style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {thumbUrl && (
              <img
                src={thumbUrl}
                alt={video.title || 'Video thumbnail'}
                loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }}
              />
            )}
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 56, height: 56, borderRadius: '50%',
              background: 'rgba(0,0,0,0.6)', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="#fff">
                <polygon points="8,5 19,12 8,19"/>
              </svg>
            </div>
          </div>
        )}

        {/* Lock overlay */}
        {locked && (
          <div style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            background: 'rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', color: '#fff',
          }}>
            <span style={{ fontSize: 32, marginBottom: 4 }}>🔒</span>
            <span style={{ fontSize: 11, fontWeight: 700 }}>Complete previous video</span>
          </div>
        )}
      </div>

      {/* Label badge top-left (skip 'optional' — only show mandatory/recommended) */}
      {label && label !== 'optional' && meta.icon && (
        <div style={{
          position: 'absolute', top: 10, left: 10,
          background: meta.bg || '#fff', color: meta.color || '#333',
          border: '1px solid ' + (meta.border || '#ddd'),
          borderRadius: 8, padding: '3px 10px', fontSize: 10, fontWeight: 700,
        }}>
          {meta.icon} {meta.label}
        </div>
      )}

      {/* Bottom gradient overlay with title + progress */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, padding: '28px 14px 12px',
        background: 'linear-gradient(0deg,rgba(0,0,0,0.88) 0%,rgba(0,0,0,0) 100%)',
      }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#fff', marginBottom: 2, lineHeight: 1.3 }}>
          {video.title}
        </div>
        {isDrive && (
          <div style={{ fontSize: 10, color: '#93c5fd', fontWeight: 600 }}>Private video</div>
        )}
        {pct > 0 && pct < 90 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <div style={{ flex: 1, height: 3, background: 'rgba(255,255,255,0.2)', borderRadius: 2 }}>
              <div style={{ height: '100%', width: pct + '%', background: '#C9A84C', borderRadius: 2 }} />
            </div>
            <span style={{ color: '#C9A84C', fontSize: 10, fontWeight: 700 }}>{pct}%</span>
          </div>
        )}
        {pct >= 90 && (
          <div style={{ fontSize: 10, color: '#4ade80', fontWeight: 700, marginTop: 2 }}>✅ Completed</div>
        )}
      </div>
    </div>
  );
}

// ── Keyword helpers ───────────────────────────────────────────────────────────
var COURSE_KEYWORDS      = ['course', 'class', 'program', 'workshop', 'training', 'learn', 'session', 'module'];
var TESTIMONIAL_KEYWORDS = ['testimonial', 'review', 'success', 'story', 'patient', 'result', 'cure', 'healed', 'recovery', 'experience'];
var PAIN_KEYWORDS        = ['pain', 'relief', 'back', 'neck', 'knee', 'joint', 'spine', 'shoulder', 'hip', 'sciatica', 'posture', 'arthritis'];

function videoMatchesKeywords(video, keywords) {
  var text = ((video.title || '') + ' ' + (video.description || '')).toLowerCase();
  return keywords.some(function(k) { return text.includes(k); });
}

function extractKeywordsFromVideo(video) {
  var text = ((video.title || '') + ' ' + (video.description || '')).toLowerCase();
  var allKeywords = COURSE_KEYWORDS.concat(TESTIMONIAL_KEYWORDS).concat(PAIN_KEYWORDS);
  return allKeywords.filter(function(k) { return text.includes(k); });
}

function categorise(videos, assignedIds) {
  var assigned = [], courses = [], testimonials = [], pain = [], other = [];
  videos.forEach(function(v) {
    if (v.source === 'drive' || assignedIds.has(v.youtubeId)) { assigned.push(Object.assign({}, v, { assigned: true, category: 'assigned' })); return; }
    if (videoMatchesKeywords(v, TESTIMONIAL_KEYWORDS)) { testimonials.push(Object.assign({}, v, { category: 'testimonial' })); return; }
    if (videoMatchesKeywords(v, COURSE_KEYWORDS))      { courses.push(Object.assign({}, v, { category: 'course' })); return; }
    if (videoMatchesKeywords(v, PAIN_KEYWORDS))        { pain.push(Object.assign({}, v, { category: 'pain' })); return; }
    other.push(Object.assign({}, v, { category: 'other' }));
  });
  return { assigned, courses, testimonials, pain, other };
}

// ── Smart Video Feed with progress tracking ──────────────────────────────────
var LABEL_META = {
  mandatory:   { icon: '\uD83D\uDD34', color: '#dc2626', bg: '#fef2f2', border: '#fca5a5', label: 'Mandatory' },
  recommended: { icon: '\uD83D\uDFE1', color: '#d97706', bg: '#fffbeb', border: '#fcd34d', label: 'Recommended' },
  optional:    { icon: '\uD83D\uDFE2', color: '#16a34a', bg: '#f0fdf4', border: '#86efac', label: 'Optional' },
};
var LABEL_PRIORITY = { mandatory: 0, recommended: 1, optional: 2 };

function getAssignmentForPatient(video, patientCard) {
  if (!video.assignedPatients) return null;
  var match = video.assignedPatients.find(function(a) {
    return (typeof a === 'string' ? a : a.patientCard) === patientCard;
  }) || null;
  if (!match) return null;
  // Filter out expired assignments
  if (match.validUntil && new Date(match.validUntil) < new Date()) return null;
  return match;
}

// ── Smart Video Card (thumbnail + progress bar + label) ──────────────────────
function SmartVideoCard({ video, progress, label, locked, onPlay, patientCard }) {
  var isDrive = video.source === 'drive';
  var pct = progress ? progress.percentWatched : 0;
  var completed = pct >= 90;
  var meta = LABEL_META[label] || LABEL_META.recommended;
  var thumbUrl = !isDrive && video.youtubeId
    ? 'https://img.youtube.com/vi/' + video.youtubeId + '/hqdefault.jpg'
    : (video.thumbnail || '');

  return (
    <div onClick={function() { if (!locked) onPlay(video); }}
      style={{
        width: 156, flexShrink: 0, cursor: locked ? 'not-allowed' : 'pointer',
        opacity: locked ? 0.5 : 1, position: 'relative',
      }}>
      {/* Thumbnail */}
      <div style={{
        position: 'relative', width: '100%', paddingTop: '56.25%',
        borderRadius: 12, overflow: 'hidden', background: '#111',
      }}>
        {thumbUrl && <img src={thumbUrl} alt={video.title || 'Video'}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
        {/* Play overlay */}
        {!locked && (
          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
            width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff"><polygon points="8,5 19,12 8,19"/></svg>
          </div>
        )}
        {/* Lock overlay */}
        {locked && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: 22 }}>{'\uD83D\uDD12'}</span>
            <span style={{ fontSize: 9, color: '#fff', marginTop: 4, fontWeight: 600 }}>Complete previous</span>
          </div>
        )}
        {/* Completed check */}
        {completed && !locked && (
          <div style={{
            position: 'absolute', top: 6, right: 6, width: 22, height: 22, borderRadius: '50%',
            background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
        )}
        {/* Progress bar */}
        {pct > 0 && pct < 90 && (
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: 'rgba(255,255,255,0.3)' }}>
            <div style={{ height: '100%', width: pct + '%', background: '#C9A84C', borderRadius: 2 }} />
          </div>
        )}
      </div>
      {/* Title + info */}
      <div style={{ padding: '6px 2px 0' }}>
        <div style={{
          fontSize: 12, fontWeight: 600, color: '#1e1b4b', lineHeight: 1.3,
          overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        }}>{video.title || 'Video'}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 }}>
          {label !== 'optional' && <span style={{
            fontSize: 9, fontWeight: 700, color: meta.color, background: meta.bg,
            border: '1px solid ' + meta.border, borderRadius: 10, padding: '1px 6px',
          }}>{meta.icon} {meta.label}</span>}
          {pct > 0 && pct < 90 && (
            <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 600 }}>{pct}%</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Horizontal scroll row of video cards ─────────────────────────────────────
function VideoRow({ videos, progressMap, patientCard, onPlay, lockedIds }) {
  return (
    <div style={{
      display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8,
      WebkitOverflowScrolling: 'touch', scrollSnapType: 'x mandatory',
    }}>
      {videos.map(function(v) {
        var assignment = getAssignmentForPatient(v, patientCard);
        var label = (assignment && assignment.label) || 'recommended';
        var prog = progressMap[v._id] || null;
        var locked = lockedIds ? lockedIds.has(v._id) : false;
        return (
          <SmartVideoCard key={v._id} video={v} progress={prog} label={label}
            locked={locked} onPlay={onPlay} patientCard={patientCard} />
        );
      })}
    </div>
  );
}

// ── Section header ───────────────────────────────────────────────────────────
function SectionHeader({ icon, title, color, count, collapsed, onToggle }) {
  return (
    <div onClick={onToggle} style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0 6px', cursor: 'pointer',
    }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <span style={{ fontSize: 13, fontWeight: 800, color: color, letterSpacing: 0.3 }}>{title}</span>
      <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
      <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>{count}</span>
      <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 2 }}>{collapsed ? '\u25BC' : '\u25B2'}</span>
    </div>
  );
}

// ── Shared helper: categorize videos into sections ────────────────────────────
function categorizeVideos(assignedVideos, assignedPlaylists, plVidsMap, watchProgressMap, patientCard) {
  var allVideos = [];
  var assignedPlIds = new Set((assignedPlaylists || []).map(function(p) { return p.playlistId; }));

  (assignedVideos || []).forEach(function(v) {
    if (!v.playlistId || !assignedPlIds.has(v.playlistId)) allVideos.push(v);
  });
  (assignedPlaylists || []).forEach(function(pl) {
    (plVidsMap[pl.playlistId] || []).forEach(function(v) {
      if (!allVideos.some(function(av) { return av._id === v._id; })) allVideos.push(v);
    });
  });

  var lockedIds = new Set();
  (assignedPlaylists || []).forEach(function(pl) {
    if (!pl.sequential) return;
    var plVids = (plVidsMap[pl.playlistId] || []).slice().sort(function(a, b) {
      var aA = getAssignmentForPatient(a, patientCard);
      var bA = getAssignmentForPatient(b, patientCard);
      return ((aA && aA.order) || 0) - ((bA && bA.order) || 0);
    });
    var foundIncomplete = false;
    plVids.forEach(function(v) {
      if (foundIncomplete) { lockedIds.add(v._id); return; }
      var prog = watchProgressMap[v._id];
      if (!prog || !prog.completed) foundIncomplete = true;
    });
  });

  var continueWatching = [], mandatory = [], recommended = [], optional = [], completed = [];
  allVideos.forEach(function(v) {
    var assignment = getAssignmentForPatient(v, patientCard);
    var label = (assignment && assignment.label) || 'recommended';
    var prog = watchProgressMap[v._id];
    var pct = prog ? prog.percentWatched : 0;
    if (pct >= 90) { completed.push(v); return; }
    if (pct >= 1) { continueWatching.push(v); return; }
    if (label === 'mandatory') mandatory.push(v);
    else if (label === 'optional') optional.push(v);
    else recommended.push(v);
  });

  continueWatching.sort(function(a, b) {
    var aA = getAssignmentForPatient(a, patientCard);
    var bA = getAssignmentForPatient(b, patientCard);
    var aPri = LABEL_PRIORITY[(aA && aA.label) || 'recommended'] || 1;
    var bPri = LABEL_PRIORITY[(bA && bA.label) || 'recommended'] || 1;
    if (aPri !== bPri) return aPri - bPri;
    var aUp = (watchProgressMap[a._id] && watchProgressMap[a._id].updatedAt) || '';
    var bUp = (watchProgressMap[b._id] && watchProgressMap[b._id].updatedAt) || '';
    return new Date(bUp) - new Date(aUp);
  });

  return { allVideos: allVideos, continueWatching: continueWatching, mandatory: mandatory,
    recommended: recommended, optional: optional, completed: completed, lockedIds: lockedIds };
}

// ── Instagram-style Home Feed ─────────────────────────────────────────────────
function HomeFeedSection({ assignedVideos, assignedPlaylists, plVidsMap, plVidsLoading, playlistFilter, watchProgressMap, setWatchProgressMap, patientCard }) {
  var _av = useState(null), activeVideo = _av[0], setActiveVideo = _av[1];
  var _sc = useState(false), showCompleted = _sc[0], setShowCompleted = _sc[1];
  var _ai = useState(0), activeIdx = _ai[0], setActiveIdx = _ai[1];
  var cardRefs = useRef([]);
  var observerRef = useRef(null);
  var progressTimerRef = useRef(null);
  var playerRef = useRef(null);
  var videoElRef = useRef(null);
  var debounceRef = useRef(null);
  var catsCache = useRef({ key: '', result: null });

  // Memoize categorizeVideos to avoid re-computing on every scroll-triggered render
  var catsKey = (assignedVideos || []).length + ':' + (assignedPlaylists || []).length + ':' + Object.keys(plVidsMap || {}).length + ':' + Object.keys(watchProgressMap || {}).length + ':' + patientCard;
  if (catsCache.current.key !== catsKey) {
    catsCache.current = { key: catsKey, result: categorizeVideos(assignedVideos, assignedPlaylists, plVidsMap, watchProgressMap, patientCard) };
  }
  var cats = catsCache.current.result;

  // Build flat list with category tags
  var flatItems = [];
  var sections = [
    { key: 'continue', label: 'Continue Watching', icon: '\u25B6\uFE0F', color: '#C9A84C', videos: cats.continueWatching },
    { key: 'mandatory', label: 'Mandatory', icon: '\uD83D\uDD34', color: '#dc2626', videos: cats.mandatory },
    { key: 'recommended', label: 'Recommended', icon: '\uD83D\uDFE1', color: '#d97706', videos: cats.recommended },
    { key: 'optional', label: 'Optional', icon: '\uD83D\uDFE2', color: '#16a34a', videos: cats.optional },
  ];
  sections.forEach(function(s) {
    s.videos.forEach(function(v) {
      var assignment = getAssignmentForPatient(v, patientCard);
      flatItems.push({ video: v, category: s.key, label: (assignment && assignment.label) || 'recommended' });
    });
  });
  if (showCompleted) {
    cats.completed.forEach(function(v) {
      var assignment = getAssignmentForPatient(v, patientCard);
      flatItems.push({ video: v, category: 'completed', label: (assignment && assignment.label) || 'recommended' });
    });
  }

  // Filter by playlist if active
  if (playlistFilter) {
    flatItems = flatItems.filter(function(item) { return item.video.playlistId === playlistFilter; });
  }

  // IntersectionObserver for auto-play (debounced to avoid excessive re-renders)
  useEffect(function() {
    if (activeVideo) return; // don't observe when player is open
    if (observerRef.current) observerRef.current.disconnect();
    var bestEntry = {};
    var obs = new IntersectionObserver(function(entries) {
      entries.forEach(function(e) {
        var idx = Number(e.target.dataset.idx);
        if (!isNaN(idx)) bestEntry[idx] = e.intersectionRatio;
        if (!e.isIntersecting) delete bestEntry[idx];
      });
      var maxIdx = -1, maxRatio = 0;
      Object.keys(bestEntry).forEach(function(k) {
        if (bestEntry[k] > maxRatio) { maxRatio = bestEntry[k]; maxIdx = Number(k); }
      });
      if (maxIdx >= 0) {
        // Debounce to prevent rapid re-renders while scrolling
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(function() { setActiveIdx(maxIdx); }, 150);
      }
    }, { threshold: [0.5] }); // Single threshold — fires once per card crossing 50% visibility
    observerRef.current = obs;
    cardRefs.current.forEach(function(el) { if (el) obs.observe(el); });
    return function() {
      obs.disconnect();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [flatItems.length, activeVideo]);

  // Progress tracking (reused from VideoFeedSection)
  function sendProgress(video, ws, ts) {
    if (!patientCard || !video || !video._id) return;
    api.post(process.env.REACT_APP_URL + '/resource-videos/watch-progress', {
      patientCard: patientCard, videoId: video._id, playlistId: video.playlistId || '',
      watchedSeconds: Math.floor(ws), totalSeconds: Math.floor(ts),
    }).then(function(res) {
      if (res.data) {
        setWatchProgressMap(function(prev) {
          var next = Object.assign({}, prev);
          next[video._id] = res.data;
          return next;
        });
      }
    }).catch(function() {});
  }

  function startProgressTimer(video) {
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    progressTimerRef.current = setInterval(function() {
      if (playerRef.current && typeof playerRef.current.getCurrentTime === 'function') {
        try { var ws = playerRef.current.getCurrentTime(); var ts = playerRef.current.getDuration(); if (ts > 0) sendProgress(video, ws, ts); } catch (e) {}
      }
      if (videoElRef.current) { var vEl = videoElRef.current; if (vEl.duration > 0) sendProgress(video, vEl.currentTime, vEl.duration); }
    }, 10000);
  }
  function stopProgressTimer() { if (progressTimerRef.current) { clearInterval(progressTimerRef.current); progressTimerRef.current = null; } }
  useEffect(function() { return function() { stopProgressTimer(); }; }, []);

  function handleTap(video) {
    stopProgressTimer();
    setActiveVideo(video);
  }

  // Loading
  if (plVidsLoading && (assignedPlaylists || []).length > 0) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: '#999' }}>
        <div style={{ fontSize: 28, marginBottom: 8, animation: 'spin 1.5s linear infinite' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2.5" strokeLinecap="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
        </div>
        Loading your feed...
      </div>
    );
  }

  if (cats.allVideos.length === 0) {
    return (
      <div style={{ padding: '48px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.4 }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.5"><rect x="2" y="3" width="20" height="18" rx="3"/><polygon points="10,8 16,12 10,16"/></svg>
        </div>
        <div style={{ fontWeight: 700, fontSize: 16, color: '#4b5563', marginBottom: 6 }}>No videos yet</div>
        <div style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.5 }}>
          Videos will appear here after your therapist assigns them.
        </div>
      </div>
    );
  }

  var allCaughtUp = cats.continueWatching.length === 0 && cats.mandatory.length === 0 && cats.recommended.length === 0 && cats.optional.length === 0;

  return (
    <div style={{ maxWidth: 500, margin: '0 auto', padding: '0 8px 80px' }}>

      {/* ── Active Video Player (full player on tap) ── */}
      {activeVideo && (
        <div style={{
          position: 'relative', width: '100%', background: '#000',
          borderRadius: 16, overflow: 'hidden', marginBottom: 16,
          boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
        }}>
          <button onClick={function() { stopProgressTimer(); setActiveVideo(null); }}
            style={{
              position: 'absolute', top: 8, right: 8, zIndex: 20,
              background: 'rgba(0,0,0,.6)', color: '#fff', border: 'none',
              borderRadius: '50%', width: 30, height: 30, fontSize: 15, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>x</button>
          <div style={{ paddingTop: '56.25%', position: 'relative' }}>
            {activeVideo.source === 'drive' ? (
              <video
                key={'play-drive-' + activeVideo._id}
                ref={function(el) {
                  videoElRef.current = el;
                  if (el) {
                    var prog = watchProgressMap[activeVideo._id];
                    if (prog && prog.watchedSeconds > 0 && prog.percentWatched < 90) el.currentTime = prog.watchedSeconds;
                    el.play().catch(function() {});
                    startProgressTimer(activeVideo);
                    el.addEventListener('ended', function() { sendProgress(activeVideo, el.duration, el.duration); stopProgressTimer(); });
                  }
                }}
                src={process.env.REACT_APP_URL + '/resource-videos/stream/' + activeVideo._id}
                controls controlsList="nodownload" playsInline
                onContextMenu={function(e) { e.preventDefault(); }}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <iframe
                key={'play-yt-' + activeVideo._id}
                ref={function(el) { if (el) startProgressTimer(activeVideo); }}
                src={'https://www.youtube.com/embed/' + activeVideo.youtubeId
                  + '?autoplay=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1'
                  + ((watchProgressMap[activeVideo._id] && watchProgressMap[activeVideo._id].watchedSeconds > 0 && watchProgressMap[activeVideo._id].percentWatched < 90)
                    ? '&start=' + Math.floor(watchProgressMap[activeVideo._id].watchedSeconds) : '')}
                title={activeVideo.title}
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
              />
            )}
          </div>
          <div style={{ padding: '10px 14px', background: '#1a1a2e' }}>
            <div style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>{activeVideo.title || 'Video'}</div>
            {watchProgressMap[activeVideo._id] && watchProgressMap[activeVideo._id].percentWatched > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                <div style={{ flex: 1, height: 3, background: 'rgba(255,255,255,0.2)', borderRadius: 2 }}>
                  <div style={{ height: '100%', width: watchProgressMap[activeVideo._id].percentWatched + '%', background: '#C9A84C', borderRadius: 2 }} />
                </div>
                <span style={{ color: '#9ca3af', fontSize: 11, fontWeight: 600 }}>{watchProgressMap[activeVideo._id].percentWatched}%</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── All Caught Up ── */}
      {allCaughtUp && !activeVideo && (
        <div style={{
          textAlign: 'center', padding: '20px 16px', margin: '0 0 16px',
          background: 'linear-gradient(135deg, #f0fdf4, #ecfdf5)', borderRadius: 14,
          border: '1px solid #bbf7d0',
        }}>
          <div style={{ fontSize: 28, marginBottom: 4 }}>{'\u2705'}</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#166534' }}>All caught up!</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>You have completed all assigned videos.</div>
        </div>
      )}

      {/* ── Instagram-style vertical feed ── */}
      {!activeVideo && flatItems.map(function(item, i) {
        var prevCat = i > 0 ? flatItems[i - 1].category : '';
        var showDivider = item.category !== prevCat;
        var sec = sections.find(function(s) { return s.key === item.category; }) || {};
        return (
          <div key={item.video._id} ref={function(el) { cardRefs.current[i] = el; }} data-idx={i}>
            {showDivider && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '12px 4px 6px',
              }}>
                <span style={{ fontSize: 14 }}>{sec.icon || (item.category === 'completed' ? '\u2705' : '')}</span>
                <span style={{ fontSize: 12, fontWeight: 800, color: sec.color || '#16a34a', letterSpacing: 0.3 }}>
                  {sec.label || 'Completed'}
                </span>
                <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
              </div>
            )}
            <FeedCard
              video={item.video}
              isActive={activeIdx === i && !cats.lockedIds.has(item.video._id)}
              isNearby={Math.abs(activeIdx - i) <= 2}
              label={item.label}
              progress={watchProgressMap[item.video._id]}
              locked={cats.lockedIds.has(item.video._id)}
              onTap={handleTap}
            />
          </div>
        );
      })}

      {/* Show completed toggle */}
      {!activeVideo && cats.completed.length > 0 && (
        <div style={{ textAlign: 'center', margin: '8px 0 16px' }}>
          <button onClick={function() { setShowCompleted(function(v) { return !v; }); }}
            style={{
              padding: '8px 20px', borderRadius: 20, border: '1.5px solid #d1d5db',
              background: showCompleted ? '#f0fdf4' : '#fff', color: '#374151',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}>
            {showCompleted ? 'Hide' : 'Show'} {cats.completed.length} completed
          </button>
        </div>
      )}

      {/* More Videos link */}
      <a
        href="https://www.youtube.com/@MedicalYogaCentre/videos"
        target="_blank" rel="noreferrer"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          margin: '20px auto 8px', padding: '12px 24px',
          background: 'linear-gradient(90deg,#ff0000,#cc0000)',
          color: '#fff', borderRadius: 28, fontWeight: 700, fontSize: 13,
          textDecoration: 'none', maxWidth: 260,
          boxShadow: '0 4px 14px rgba(200,0,0,0.35)',
        }}
      >
        More Videos on YouTube
      </a>
    </div>
  );
}

// ── Playlists Tab Section ─────────────────────────────────────────────────────
function PlaylistsTabSection({ assignedVideos, assignedPlaylists, plVidsMap, plVidsLoading, watchProgressMap, setWatchProgressMap, patientCard, playlistFilter, setPlaylistFilter, setBottomTab, videos }) {
  var _sp = useState(false), showPlaylists = _sp[0], setShowPlaylists = _sp[1];
  var _av = useState(null), activeVideo = _av[0], setActiveVideo = _av[1];
  var progressTimerRef = useRef(null);
  var videoElRef = useRef(null);
  var catsCache = useRef({ key: '', result: null });

  // Memoize categorizeVideos
  var catsKey = (assignedVideos || []).length + ':' + (assignedPlaylists || []).length + ':' + Object.keys(plVidsMap || {}).length + ':' + Object.keys(watchProgressMap || {}).length + ':' + patientCard;
  if (catsCache.current.key !== catsKey) {
    catsCache.current = { key: catsKey, result: categorizeVideos(assignedVideos, assignedPlaylists, plVidsMap, watchProgressMap, patientCard) };
  }
  var cats = catsCache.current.result;

  // Progress tracking for when user taps a card
  function sendProgress(video, ws, ts) {
    if (!patientCard || !video || !video._id) return;
    api.post(process.env.REACT_APP_URL + '/resource-videos/watch-progress', {
      patientCard: patientCard, videoId: video._id, playlistId: video.playlistId || '',
      watchedSeconds: Math.floor(ws), totalSeconds: Math.floor(ts),
    }).then(function(res) {
      if (res.data) { setWatchProgressMap(function(prev) { var n = Object.assign({}, prev); n[video._id] = res.data; return n; }); }
    }).catch(function() {});
  }
  function startProgressTimer(video) {
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    progressTimerRef.current = setInterval(function() {
      if (videoElRef.current) { var vEl = videoElRef.current; if (vEl.duration > 0) sendProgress(video, vEl.currentTime, vEl.duration); }
    }, 10000);
  }
  function stopProgressTimer() { if (progressTimerRef.current) { clearInterval(progressTimerRef.current); progressTimerRef.current = null; } }
  useEffect(function() { return function() { stopProgressTimer(); }; }, []);

  function handlePlay(video) { stopProgressTimer(); setActiveVideo(video); }

  var sectionList = [
    { key: 'continue', icon: '\u25B6\uFE0F', title: 'Continue Watching', color: '#C9A84C', videos: cats.continueWatching },
    { key: 'mandatory', icon: '\uD83D\uDD34', title: 'Mandatory', color: '#dc2626', videos: cats.mandatory },
    { key: 'recommended', icon: '\uD83D\uDFE1', title: 'Recommended', color: '#d97706', videos: cats.recommended },
    { key: 'optional', icon: '\uD83D\uDFE2', title: 'Optional', color: '#16a34a', videos: cats.optional },
    { key: 'completed', icon: '\u2705', title: 'Completed', color: '#16a34a', videos: cats.completed },
  ];

  return (
    <div style={{ padding: '12px 14px' }}>

      {/* ── Active Video Player ── */}
      {activeVideo && (
        <div style={{ position: 'relative', width: '100%', background: '#000', borderRadius: 16, overflow: 'hidden', marginBottom: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.4)' }}>
          <button onClick={function() { stopProgressTimer(); setActiveVideo(null); }}
            style={{ position: 'absolute', top: 8, right: 8, zIndex: 20, background: 'rgba(0,0,0,.6)', color: '#fff', border: 'none', borderRadius: '50%', width: 30, height: 30, fontSize: 15, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>x</button>
          <div style={{ paddingTop: '56.25%', position: 'relative' }}>
            {activeVideo.source === 'drive' ? (
              <video key={'pl-drive-' + activeVideo._id}
                ref={function(el) { videoElRef.current = el; if (el) { var prog = watchProgressMap[activeVideo._id]; if (prog && prog.watchedSeconds > 0 && prog.percentWatched < 90) el.currentTime = prog.watchedSeconds; el.play().catch(function(){}); startProgressTimer(activeVideo); el.addEventListener('ended', function(){ sendProgress(activeVideo, el.duration, el.duration); stopProgressTimer(); }); } }}
                src={process.env.REACT_APP_URL + '/resource-videos/stream/' + activeVideo._id}
                controls controlsList="nodownload" playsInline onContextMenu={function(e){e.preventDefault();}}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <iframe key={'pl-yt-' + activeVideo._id}
                ref={function(el) { if (el) startProgressTimer(activeVideo); }}
                src={'https://www.youtube.com/embed/' + activeVideo.youtubeId + '?autoplay=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1' + ((watchProgressMap[activeVideo._id] && watchProgressMap[activeVideo._id].watchedSeconds > 0 && watchProgressMap[activeVideo._id].percentWatched < 90) ? '&start=' + Math.floor(watchProgressMap[activeVideo._id].watchedSeconds) : '')}
                title={activeVideo.title} allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} />
            )}
          </div>
          <div style={{ padding: '10px 14px', background: '#1a1a2e' }}>
            <div style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>{activeVideo.title || 'Video'}</div>
          </div>
        </div>
      )}

      {/* ── Video Categories (always expanded) ── */}
      {sectionList.map(function(s) {
        if (s.videos.length === 0) return null;
        return (
          <div key={s.key} style={{ marginBottom: 16 }}>
            <SectionHeader icon={s.icon} title={s.title} color={s.color}
              count={s.videos.length} collapsed={false} onToggle={function() {}} />
            <VideoRow videos={s.videos} progressMap={watchProgressMap}
              patientCard={patientCard} onPlay={handlePlay} lockedIds={cats.lockedIds} />
          </div>
        );
      })}

      {/* ── Divider ── */}
      <div style={{ height: 1, background: '#e5e7eb', margin: '16px 0' }} />

      {/* ── My Playlists (toggle) ── */}
      <div onClick={function() { setShowPlaylists(function(v) { return !v; }); }}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 0', cursor: 'pointer',
        }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: '#1a1d23' }}>
          {'\uD83C\uDFB5'} My Playlists
        </span>
        <span style={{ fontSize: 12, color: '#9ca3af' }}>{showPlaylists ? '\u25B2' : '\u25BC'}</span>
      </div>

      {showPlaylists && (
        <>
          {/* All Videos option */}
          <div
            onClick={function() { setPlaylistFilter(null); setBottomTab('home'); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
              borderRadius: 14, marginBottom: 10, cursor: 'pointer',
              background: !playlistFilter ? 'rgba(108,92,231,0.08)' : '#f9fafb',
              border: !playlistFilter ? '1.5px solid rgba(108,92,231,0.3)' : '1.5px solid #e5e7eb',
            }}
          >
            <div style={{ width: 44, height: 44, borderRadius: 12, background: '#6C5CE7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><polygon points="5,3 19,12 5,21"/></svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1d23' }}>All Videos</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>{(videos || []).length} videos</div>
            </div>
            {!playlistFilter && <span style={{ color: '#6C5CE7', fontWeight: 700, fontSize: 13 }}>Active</span>}
          </div>

          {/* Assigned playlists */}
          {(assignedPlaylists || []).map(function(pl, i) {
            var colors = ['#7c3aed', '#2563eb', '#059669', '#d97706', '#dc2626', '#0891b2'];
            var color = colors[i % colors.length];
            var isActive = playlistFilter === pl.playlistId;
            return (
              <div key={pl.playlistId}
                onClick={function() { setPlaylistFilter(pl.playlistId); setBottomTab('home'); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                  borderRadius: 14, marginBottom: 10, cursor: 'pointer',
                  background: isActive ? color + '12' : '#f9fafb',
                  border: isActive ? '1.5px solid ' + color + '55' : '1.5px solid #e5e7eb',
                }}
              >
                <div style={{ width: 44, height: 44, borderRadius: 12, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><rect x="2" y="2" width="20" height="20" rx="3"/><polygon points="10,8 16,12 10,16"/></svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1d23' }}>{pl.title || 'Playlist'}</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>{pl.videoCount || 0} videos</div>
                </div>
                {isActive && <span style={{ color: color, fontWeight: 700, fontSize: 13 }}>Active</span>}
              </div>
            );
          })}

          {(assignedPlaylists || []).length === 0 && (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40, fontSize: 13 }}>
              No playlists assigned yet
            </div>
          )}
        </>
      )}
    </div>
  );
}

function VideoFeedSection({ assignedVideos, assignedPlaylists, playlistFilter, watchProgressMap, setWatchProgressMap, patientCard }) {
  var [activeVideo, setActiveVideo] = useState(null); // video being played
  var [collapsedSections, setCollapsedSections] = useState({ completed: true });
  var progressTimerRef = useRef(null);
  var playerRef = useRef(null);
  var videoElRef = useRef(null);

  // Fetch playlist videos
  var [plVidsMap, setPlVidsMap] = useState({});
  var [plVidsLoading, setPlVidsLoading] = useState(true);

  useEffect(function() {
    var pls = assignedPlaylists || [];
    if (pls.length === 0) { setPlVidsLoading(false); return; }
    setPlVidsLoading(true);
    Promise.all(pls.map(function(pl) {
      return api.get(process.env.REACT_APP_URL + '/youtube/playlist-videos/' + pl.playlistId)
        .then(function(r) { return { playlistId: pl.playlistId, videos: r.data || [] }; })
        .catch(function() { return { playlistId: pl.playlistId, videos: [] }; });
    })).then(function(results) {
      var map = {};
      results.forEach(function(r) { map[r.playlistId] = r.videos; });
      setPlVidsMap(map);
      setPlVidsLoading(false);
    });
  }, [assignedPlaylists]);

  // ── Build all video list (loose + playlist) ──
  var allVideos = [];
  var assignedPlIds = new Set((assignedPlaylists || []).map(function(p) { return p.playlistId; }));

  // Loose videos (not in any assigned playlist)
  (assignedVideos || []).forEach(function(v) {
    if (!v.playlistId || !assignedPlIds.has(v.playlistId)) allVideos.push(v);
  });
  // Playlist videos
  (assignedPlaylists || []).forEach(function(pl) {
    (plVidsMap[pl.playlistId] || []).forEach(function(v) {
      if (!allVideos.some(function(av) { return av._id === v._id; })) allVideos.push(v);
    });
  });

  // ── Determine locked videos (sequential playlists) ──
  var lockedIds = new Set();
  (assignedPlaylists || []).forEach(function(pl) {
    if (!pl.sequential) return;
    var plVids = (plVidsMap[pl.playlistId] || []).slice().sort(function(a, b) {
      var aAssign = getAssignmentForPatient(a, patientCard);
      var bAssign = getAssignmentForPatient(b, patientCard);
      return ((aAssign && aAssign.order) || 0) - ((bAssign && bAssign.order) || 0);
    });
    var foundIncomplete = false;
    plVids.forEach(function(v) {
      if (foundIncomplete) { lockedIds.add(v._id); return; }
      var prog = watchProgressMap[v._id];
      if (!prog || !prog.completed) foundIncomplete = true;
    });
  });

  // ── Categorize videos ──
  var continueWatching = [], mandatory = [], recommended = [], optional = [], completed = [];

  allVideos.forEach(function(v) {
    var assignment = getAssignmentForPatient(v, patientCard);
    var label = (assignment && assignment.label) || 'recommended';
    var prog = watchProgressMap[v._id];
    var pct = prog ? prog.percentWatched : 0;

    if (pct >= 90) { completed.push(v); return; }
    if (pct >= 1) { continueWatching.push(v); return; }
    // Unstarted — categorize by label
    if (label === 'mandatory') mandatory.push(v);
    else if (label === 'optional') optional.push(v);
    else recommended.push(v);
  });

  // Sort continue watching: mandatory first, then by updatedAt desc
  continueWatching.sort(function(a, b) {
    var aAssign = getAssignmentForPatient(a, patientCard);
    var bAssign = getAssignmentForPatient(b, patientCard);
    var aPri = LABEL_PRIORITY[(aAssign && aAssign.label) || 'recommended'] || 1;
    var bPri = LABEL_PRIORITY[(bAssign && bAssign.label) || 'recommended'] || 1;
    if (aPri !== bPri) return aPri - bPri;
    var aUp = (watchProgressMap[a._id] && watchProgressMap[a._id].updatedAt) || '';
    var bUp = (watchProgressMap[b._id] && watchProgressMap[b._id].updatedAt) || '';
    return new Date(bUp) - new Date(aUp);
  });

  // ── Progress tracking ──
  function sendProgress(video, ws, ts) {
    if (!patientCard || !video || !video._id) return;
    var playlistId = video.playlistId || '';
    api.post(process.env.REACT_APP_URL + '/resource-videos/watch-progress', {
      patientCard: patientCard, videoId: video._id, playlistId: playlistId,
      watchedSeconds: Math.floor(ws), totalSeconds: Math.floor(ts),
    }).then(function(res) {
      if (res.data) {
        setWatchProgressMap(function(prev) {
          var next = Object.assign({}, prev);
          next[video._id] = res.data;
          return next;
        });
      }
    }).catch(function() {});
  }

  function startProgressTimer(video) {
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    progressTimerRef.current = setInterval(function() {
      // YouTube player
      if (playerRef.current && typeof playerRef.current.getCurrentTime === 'function') {
        try {
          var ws = playerRef.current.getCurrentTime();
          var ts = playerRef.current.getDuration();
          if (ts > 0) sendProgress(video, ws, ts);
        } catch (e) {}
      }
      // Drive video
      if (videoElRef.current) {
        var vEl = videoElRef.current;
        if (vEl.duration > 0) sendProgress(video, vEl.currentTime, vEl.duration);
      }
    }, 10000); // every 10s
  }

  function stopProgressTimer() {
    if (progressTimerRef.current) { clearInterval(progressTimerRef.current); progressTimerRef.current = null; }
  }

  // Cleanup on unmount
  useEffect(function() { return function() { stopProgressTimer(); }; }, []);

  // ── Play video handler ──
  function handlePlay(video) {
    stopProgressTimer();
    setActiveVideo(video);
  }

  // ── Start Over ──
  function handleStartOver(video) {
    sendProgress(video, 0, (watchProgressMap[video._id] && watchProgressMap[video._id].totalSeconds) || 1);
    setActiveVideo(video);
  }

  function toggleSection(key) {
    setCollapsedSections(function(prev) {
      var next = Object.assign({}, prev);
      next[key] = !prev[key];
      return next;
    });
  }

  // ── Loading ──
  if (plVidsLoading && (assignedPlaylists || []).length > 0) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: '#999' }}>
        <div style={{ fontSize: 28, marginBottom: 8, animation: 'spin 1.5s linear infinite' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2.5" strokeLinecap="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
        </div>
        Loading your feed...
      </div>
    );
  }

  if (allVideos.length === 0) {
    return (
      <div style={{ padding: '48px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.4 }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.5"><rect x="2" y="3" width="20" height="18" rx="3"/><polygon points="10,8 16,12 10,16"/></svg>
        </div>
        <div style={{ fontWeight: 700, fontSize: 16, color: '#4b5563', marginBottom: 6 }}>No videos yet</div>
        <div style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.5 }}>
          Videos will appear here after your therapist assigns them.<br/>
          Check back after your next session.
        </div>
      </div>
    );
  }

  // ── All caught up ──
  var allCaughtUp = continueWatching.length === 0 && mandatory.length === 0 && recommended.length === 0 && optional.length === 0;

  return (
    <div style={{ maxWidth: 500, margin: '0 auto', padding: '0 8px 80px' }}>

      {/* ── Active Video Player ── */}
      {activeVideo && (
        <div style={{
          position: 'relative', width: '100%', background: '#000',
          borderRadius: 16, overflow: 'hidden', marginBottom: 16,
          boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
        }}>
          <button onClick={function() { stopProgressTimer(); setActiveVideo(null); }}
            style={{
              position: 'absolute', top: 8, right: 8, zIndex: 20,
              background: 'rgba(0,0,0,.6)', color: '#fff', border: 'none',
              borderRadius: '50%', width: 30, height: 30, fontSize: 15, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>x</button>
          <div style={{ paddingTop: '56.25%', position: 'relative' }}>
            {activeVideo.source === 'drive' ? (
              <video
                key={'play-drive-' + activeVideo._id}
                ref={function(el) {
                  videoElRef.current = el;
                  if (el) {
                    var prog = watchProgressMap[activeVideo._id];
                    if (prog && prog.watchedSeconds > 0 && prog.percentWatched < 90) {
                      el.currentTime = prog.watchedSeconds;
                    }
                    el.play().catch(function() {});
                    startProgressTimer(activeVideo);
                    el.addEventListener('ended', function() {
                      sendProgress(activeVideo, el.duration, el.duration);
                      stopProgressTimer();
                    });
                  }
                }}
                src={process.env.REACT_APP_URL + '/resource-videos/stream/' + activeVideo._id}
                controls controlsList="nodownload" playsInline
                onContextMenu={function(e) { e.preventDefault(); }}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <iframe
                key={'play-yt-' + activeVideo._id}
                ref={function(el) { if (el) { startProgressTimer(activeVideo); } }}
                src={'https://www.youtube.com/embed/' + activeVideo.youtubeId
                  + '?autoplay=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1'
                  + ((watchProgressMap[activeVideo._id] && watchProgressMap[activeVideo._id].watchedSeconds > 0 && watchProgressMap[activeVideo._id].percentWatched < 90)
                    ? '&start=' + Math.floor(watchProgressMap[activeVideo._id].watchedSeconds) : '')}
                title={activeVideo.title}
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
              />
            )}
          </div>
          <div style={{ padding: '10px 14px', background: '#1a1a2e' }}>
            <div style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>{activeVideo.title || 'Video'}</div>
            {watchProgressMap[activeVideo._id] && watchProgressMap[activeVideo._id].percentWatched > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                <div style={{ flex: 1, height: 3, background: 'rgba(255,255,255,0.2)', borderRadius: 2 }}>
                  <div style={{ height: '100%', width: watchProgressMap[activeVideo._id].percentWatched + '%', background: '#C9A84C', borderRadius: 2 }} />
                </div>
                <span style={{ color: '#9ca3af', fontSize: 11, fontWeight: 600 }}>{watchProgressMap[activeVideo._id].percentWatched}%</span>
              </div>
            )}
            {watchProgressMap[activeVideo._id] && watchProgressMap[activeVideo._id].percentWatched > 5 && (
              <button onClick={function() { handleStartOver(activeVideo); }}
                style={{
                  marginTop: 8, padding: '5px 14px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                  background: 'rgba(255,255,255,0.1)', color: '#9ca3af', border: '1px solid rgba(255,255,255,0.15)',
                  cursor: 'pointer',
                }}>Start Over</button>
            )}
          </div>
        </div>
      )}

      {/* ── All Caught Up Banner ── */}
      {allCaughtUp && (
        <div style={{
          textAlign: 'center', padding: '20px 16px', margin: '0 0 16px',
          background: 'linear-gradient(135deg, #f0fdf4, #ecfdf5)', borderRadius: 14,
          border: '1px solid #bbf7d0',
        }}>
          <div style={{ fontSize: 28, marginBottom: 4 }}>{'\u2705'}</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#166534' }}>All caught up!</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>You have completed all assigned videos.</div>
        </div>
      )}

      {/* ── Continue Watching ── */}
      {continueWatching.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeader icon={'\u25B6\uFE0F'} title="Continue Watching" color="#C9A84C"
            count={continueWatching.length} collapsed={!!collapsedSections.continue}
            onToggle={function() { toggleSection('continue'); }} />
          {!collapsedSections.continue && (
            <VideoRow videos={continueWatching} progressMap={watchProgressMap}
              patientCard={patientCard} onPlay={handlePlay} lockedIds={lockedIds} />
          )}
        </div>
      )}

      {/* ── Mandatory ── */}
      {mandatory.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeader icon={'\uD83D\uDD34'} title="Mandatory" color="#dc2626"
            count={mandatory.length} collapsed={!!collapsedSections.mandatory}
            onToggle={function() { toggleSection('mandatory'); }} />
          {!collapsedSections.mandatory && (
            <VideoRow videos={mandatory} progressMap={watchProgressMap}
              patientCard={patientCard} onPlay={handlePlay} lockedIds={lockedIds} />
          )}
        </div>
      )}

      {/* ── Recommended ── */}
      {recommended.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeader icon={'\uD83D\uDFE1'} title="Recommended" color="#d97706"
            count={recommended.length} collapsed={!!collapsedSections.recommended}
            onToggle={function() { toggleSection('recommended'); }} />
          {!collapsedSections.recommended && (
            <VideoRow videos={recommended} progressMap={watchProgressMap}
              patientCard={patientCard} onPlay={handlePlay} lockedIds={lockedIds} />
          )}
        </div>
      )}

      {/* ── Optional ── */}
      {optional.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeader icon={'\uD83D\uDFE2'} title="Optional" color="#16a34a"
            count={optional.length} collapsed={!!collapsedSections.optional}
            onToggle={function() { toggleSection('optional'); }} />
          {!collapsedSections.optional && (
            <VideoRow videos={optional} progressMap={watchProgressMap}
              patientCard={patientCard} onPlay={handlePlay} lockedIds={lockedIds} />
          )}
        </div>
      )}

      {/* ── Completed ── */}
      {completed.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <SectionHeader icon={'\u2705'} title="Completed" color="#16a34a"
            count={completed.length} collapsed={collapsedSections.completed !== false}
            onToggle={function() { toggleSection('completed'); }} />
          {collapsedSections.completed === false && (
            <VideoRow videos={completed} progressMap={watchProgressMap}
              patientCard={patientCard} onPlay={handlePlay} lockedIds={lockedIds} />
          )}
        </div>
      )}

      {/* More Videos link */}
      <a
        href="https://www.youtube.com/@MedicalYogaCentre/videos"
        target="_blank" rel="noreferrer"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          margin: '20px auto 8px', padding: '12px 24px',
          background: 'linear-gradient(90deg,#ff0000,#cc0000)',
          color: '#fff', borderRadius: 28, fontWeight: 700, fontSize: 13,
          textDecoration: 'none', maxWidth: 260,
          boxShadow: '0 4px 14px rgba(200,0,0,0.35)',
        }}
      >
        More Videos on YouTube
      </a>
    </div>
  );
}

// ── Files Section for Saadhak (assigned files) ──────────────────────────────
function FilesFeedSection({ patientCard }) {
  var _f = useState([]), files = _f[0], setFiles = _f[1];
  var _l = useState(true), loading = _l[0], setLoading = _l[1];

  useEffect(function() {
    if (!patientCard) return;
    setLoading(true);
    api.get(process.env.REACT_APP_URL + '/resource-files/patient/' + patientCard)
      .then(function(r) { setFiles(r.data || []); })
      .catch(function() { setFiles([]); })
      .finally(function() { setLoading(false); });
  }, [patientCard]);

  function openFile(f) {
    if (f.isLink && f.linkUrl) { window.open(f.linkUrl, '_blank'); return; }
    api.get(process.env.REACT_APP_URL + '/resource-files/stream/' + f._id, { responseType: 'blob' })
      .then(function(r) {
        var url = URL.createObjectURL(new Blob([r.data], { type: f.mimeType }));
        window.open(url, '_blank');
      })
      .catch(function() { showSdToast('Could not open file.', 'error'); });
  }

  var iconColors = { pdf: '#ef4444', document: '#3b82f6', spreadsheet: '#22c55e', presentation: '#f59e0b', image: '#a855f7', link: '#0891b2', other: '#6b7280' };
  var iconLabels = { pdf: 'PDF', document: 'DOC', spreadsheet: 'XLS', presentation: 'PPT', image: 'IMG', link: 'LINK', other: 'FILE' };

  function formatSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: '#999', fontSize: 13 }}>
        Loading files...
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div style={{ padding: '32px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 8, opacity: 0.35 }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/></svg>
        </div>
        <div style={{ fontWeight: 600, fontSize: 14, color: '#6b7280' }}>No files assigned</div>
        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
          Your therapist will share documents here when needed.
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 440, margin: '0 auto', padding: '8px 8px 80px' }}>
      <div style={{
        fontSize: 12, fontWeight: 800, color: '#C9A84C', letterSpacing: 0.8,
        textTransform: 'uppercase', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <div style={{ width: 4, height: 18, borderRadius: 2, background: '#C9A84C' }} />
        Your Documents
        <div style={{ flex: 1, height: 1, background: 'rgba(0,0,0,0.06)' }} />
        <span style={{ color: '#9ca3af', fontWeight: 600, textTransform: 'none', fontSize: 11 }}>{files.length}</span>
      </div>

      {files.map(function(f) {
        var cat = f.category || 'other';
        return (
          <div key={f._id}
            onClick={function() { openFile(f); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
              background: '#fff', borderRadius: 12, marginBottom: 8,
              border: '1px solid rgba(0,0,0,0.06)', cursor: 'pointer',
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            }}
          >
            {/* File icon */}
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: (iconColors[cat] || '#6b7280') + '18',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, fontSize: 10, color: iconColors[cat] || '#6b7280',
              flexShrink: 0,
            }}>
              {iconLabels[cat] || 'FILE'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {f.title || f.filename}
              </div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                {f.isLink ? 'External link' : formatSize(f.size)}
                {!f.isLink && f.encrypted && ' \u00B7 Encrypted'}
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#C9A84C', fontWeight: 600, flexShrink: 0 }}>
              Open
            </div>
          </div>
        );
      })}
    </div>
  );
}

const getNextSession = (sessions) => {
  const now = new Date();
  return sessions
    .filter(s => new Date(s.date) > now)
    .sort((a, b) => new Date(a.date) - new Date(b.date))[0] || null;
};

const daysUntil = (dateStr) => Math.ceil((new Date(dateStr) - new Date()) / 86400000);

const getMonthCount = (sessions, monthOffset) => {
  const now    = new Date();
  const target = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  return sessions.filter(s => {
    const d = new Date(s.date);
    return d.getMonth() === target.getMonth() && d.getFullYear() === target.getFullYear();
  }).length;
};

const getLast7Days = (sessions) => {
  const DAY = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const now  = new Date();
  return Array.from({ length: 7 }, function(_, i) {
    var d = new Date(now);
    d.setDate(d.getDate() - (6 - i));
    var dStr = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    return {
      label:  DAY[d.getDay()],
      active: sessions.some(function(s) { var sd = new Date(s.date); return sd.getFullYear() + '-' + String(sd.getMonth() + 1).padStart(2, '0') + '-' + String(sd.getDate()).padStart(2, '0') === dStr; }),
      today:  i === 6,
    };
  });
};

function accumulateData(bills) {
  var paymentModeCount = {}, orgSpent = {}, serviceStats = {}, dateCounts = {}, bundleCounts = {};
  bills.forEach(function(b) {
    paymentModeCount[b.paymentmode] = (paymentModeCount[b.paymentmode] || 0) + 1;
    orgSpent[b.billorg] = (orgSpent[b.billorg] || 0) + b.total;
    var svcNames = b.services.map(function(s) { return s.name; }).sort();
    svcNames.forEach(function(name, idx) {
      serviceStats[name] = serviceStats[name] || { qty: 0 };
      serviceStats[name].qty += b.services[idx] ? (b.services[idx].qty || 0) : 0;
    });
    for (var i = 0; i < svcNames.length - 1; i++) {
      var combo = svcNames[i] + ' + ' + svcNames[i + 1];
      bundleCounts[combo] = (bundleCounts[combo] || 0) + 1;
    }
    var day = new Date(b.invoicedate).toISOString().split('T')[0];
    dateCounts[day] = (dateCounts[day] || 0) + 1;
  });
  var totalSpent = bills.reduce(function(a, b) { return a + b.total; }, 0);
  return {
    totalBills:  bills.length,
    totalSpent:  totalSpent,
    avgSpend:    bills.length ? (totalSpent / bills.length).toFixed(0) : 0,
    pieData:     Object.entries(paymentModeCount).map(function(e) { return { name: e[0], value: e[1] }; }),
    barOrgData:  Object.entries(orgSpent).map(function(e) { return { org: e[0], spend: e[1] }; }),
    serviceData: Object.entries(serviceStats).map(function(e) { return { name: e[0], qty: e[1].qty }; }),
    lineData:    Object.entries(dateCounts).sort().map(function(e) { return { date: e[0], visits: e[1] }; }),
    bundleData:  Object.entries(bundleCounts).map(function(e) { return { combo: e[0], count: e[1] }; }),
  };
}

// ── Light DataTable theme ─────────────────────────────────────────────────────
const darkTable = {
  table:      { style: { background: 'transparent' } },
  headRow:    { style: { background: 'rgba(108,92,231,0.06)', borderBottomColor: 'rgba(108,92,231,0.14)', minHeight: '42px' } },
  headCells:  { style: { color: '#6C5CE7', fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase', paddingLeft: '12px' } },
  rows:       { style: { background: 'transparent', borderBottomColor: 'rgba(0,0,0,0.06)', minHeight: '46px', '&:hover': { background: 'rgba(108,92,231,0.04)' } } },
  cells:      { style: { color: '#2d3436', fontSize: '0.83rem', paddingLeft: '12px' } },
  pagination: { style: { background: 'transparent', color: '#6C5CE7', borderTopColor: 'rgba(0,0,0,0.08)', fontSize: '0.8rem' } },
  expanderRow:    { style: { background: 'rgba(108,92,231,0.03)' } },
  expanderButton: { style: { color: '#6C5CE7' } },
  noData:     { style: { background: 'transparent', color: 'rgba(45,52,54,0.40)' } },
};

// ── Expandable rows ───────────────────────────────────────────────────────────
const BillExpandRow = ({ data }) => (
  <div className="sd-expand-row">
    <div className="sd-expand-grid">
      <div><span>Bill ID</span><strong>{data.invoicenumber}</strong></div>
      <div><span>Invoice Date</span><strong>{new Date(data.invoicedate).toLocaleDateString('en-GB')}</strong></div>
      <div><span>Payment Mode</span><strong>{data.paymentmode}</strong></div>
      <div><span>Cashier</span><strong>{data.cashier || '—'}</strong></div>
      <div><span>Total</span><strong>₹{data.total}</strong></div>
      <div><span>Given Back</span><strong>₹{data.givenback}</strong></div>
    </div>
    <div className="sd-expand-services">
      <strong>Services</strong>
      <ul>
        {data.services.map((svc, i) => (
          <li key={i}>{svc.name} — Qty: {svc.qty} | ₹{svc.amount}{svc.sdiscount ? ' (Disc: ' + svc.sdiscount + ')' : ''}</li>
        ))}
      </ul>
    </div>
  </div>
);

const SessionExpandRow = ({ data }) => (
  <div className="sd-expand-row">
    <div className="sd-expand-grid">
      {data.sessionComments  && <div><span>Comments</span><strong>{data.sessionComments}</strong></div>}
      {data.helperTherapist1 && <div><span>Therapist 1</span><strong>{data.helperTherapist1}</strong></div>}
      {data.helperTherapist2 && <div><span>Therapist 2</span><strong>{data.helperTherapist2}</strong></div>}
    </div>
  </div>
);

// ── Tooltip styles for recharts ───────────────────────────────────────────────
const ttStyle = { background: '#ffffff', border: '1px solid rgba(108,92,231,0.18)', borderRadius: 10, color: '#2d3436', fontSize: '0.82rem', boxShadow: '0 4px 16px rgba(0,0,0,0.10)' };

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function SaadhakDashboard() {
  const location = useLocation();
  const navigate = useNavigate();

  // Persist profile across navigation — save to sessionStorage on first load,
  // restore if location.state is missing (e.g. after navigating to ChangePassword and back)
  var passed = location.state ? location.state.profile : null;
  if (passed) {
    try { sessionStorage.setItem('saadhakProfile', JSON.stringify(passed)); } catch (_) {}
  } else {
    try { passed = JSON.parse(sessionStorage.getItem('saadhakProfile') || 'null'); } catch (_) { passed = null; }
  }

  const [isLoggedIn]    = useState(!!passed);
  const [profile]       = useState(passed);
  const [patientData,   setPatientData]  = useState(passed);
  const [bills,         setBills]        = useState([]);
  const [sessions,      setSessions]     = useState([]);
  const [videos,        setVideos]       = useState([]);
  const [assignedPlaylists, setAssignedPlaylists] = useState([]);
  const [watchProgressMap, setWatchProgressMap] = useState({}); // videoId → { watchedSeconds, totalSeconds, percentWatched, completed }
  const [dataReady,     setDataReady]    = useState(false);
  const [activeTab,  setActiveTab] = useState('feed');
  const [showBooking, setShowBooking] = useState(false);
  const [showCourses, setShowCourses] = useState(false);
  const [showPayQR,   setShowPayQR]   = useState(false);
  const [showContactUs, setShowContactUs] = useState(false);
  const [sdToast, setSdToast] = useState(null); // { msg, type: 'success'|'error'|'info' }
  const sdToastTimer = useRef(null);
  function showSdToast(msg, type) {
    if (sdToastTimer.current) clearTimeout(sdToastTimer.current);
    setSdToast({ msg: msg, type: type || 'info' });
    sdToastTimer.current = setTimeout(function() { setSdToast(null); }, 4000);
  }
  // Detect mobile (<= 768px) to avoid rendering HomeFeedSection in BOTH
  // sd-mobile-content AND sd-desktop-content — hidden iframes still play audio.
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);
  useEffect(function() {
    function onResize() { setIsMobile(window.innerWidth <= 768); }
    window.addEventListener('resize', onResize);
    return function() { window.removeEventListener('resize', onResize); };
  }, []);

  // Instagram-style bottom nav (mobile only)
  const [bottomTab,       setBottomTab]       = useState('home');
  const [playlistFilter,  setPlaylistFilter]  = useState(null);
  const [searchQuery,     setSearchQuery]     = useState('');
  const [notifications,   setNotifications]   = useState([]);
  const [notifLoading,    setNotifLoading]    = useState(false);

  // Playlist videos map — lifted here so HomeFeedSection + PlaylistsTabSection can share
  const [plVidsMap,      setPlVidsMap]      = useState({});
  const [plVidsLoading,  setPlVidsLoading]  = useState(true);

  useEffect(function() {
    var pls = assignedPlaylists || [];
    if (pls.length === 0) { setPlVidsLoading(false); return; }
    setPlVidsLoading(true);
    Promise.all(pls.map(function(pl) {
      return api.get(process.env.REACT_APP_URL + '/youtube/playlist-videos/' + pl.playlistId)
        .then(function(r) { return { playlistId: pl.playlistId, videos: r.data || [] }; })
        .catch(function() { return { playlistId: pl.playlistId, videos: [] }; });
    })).then(function(results) {
      var map = {};
      results.forEach(function(r) { map[r.playlistId] = r.videos; });
      setPlVidsMap(map);
      setPlVidsLoading(false);
    });
  }, [assignedPlaylists]);

  // Session: 90-day token, no idle timer — logout only on token expiry or BE restart

  useEffect(function() {
    if (isLoggedIn && profile) fetchAllData(profile.patientCard);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch notifications on load (for badge count)
  useEffect(function() {
    if (!profile || !profile.patientCard) return;
    api.get(process.env.REACT_APP_URL + '/notifications/patient/' + profile.patientCard)
      .then(function(r) { setNotifications(r.data || []); })
      .catch(function() {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  // SSE real-time connection for patient notifications
  useEffect(function() {
    if (!profile || !profile.patientCard) return;
    var token = sessionStorage.getItem('token');
    if (!token) return;
    var baseUrl = process.env.REACT_APP_URL || '';
    var sseUrl = baseUrl + '/notifications/stream?token=' + encodeURIComponent(token) + '&u=' + encodeURIComponent(profile.patientCard);
    var es;
    try { es = new EventSource(sseUrl); } catch(e) { return; }

    es.addEventListener('new_notification', function(e) {
      try {
        var notif = JSON.parse(e.data);
        setNotifications(function(prev) {
          var exists = prev.some(function(n) { return n._id === notif._id; });
          return exists ? prev : [notif].concat(prev);
        });
        // Play notification sound
        try {
          var audio = new Audio('/session-over-1.mp3');
          audio.volume = 0.5;
          audio.play().catch(function() {});
        } catch(ae) {}
      } catch(err) {}
    });

    es.addEventListener('notifications', function(e) {
      try {
        var data = JSON.parse(e.data);
        if (Array.isArray(data)) setNotifications(data);
      } catch(err) {}
    });

    es.onerror = function() { /* EventSource auto-reconnects */ };
    return function() { es.close(); };
  }, [profile]);

  // Web Push subscription for patient — always re-subscribe to handle stale/expired subscriptions
  useEffect(function() {
    if (!profile || !profile.patientCard) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    var username = profile.patientCard;

    function subscribePush() {
      Notification.requestPermission().then(function(permission) {
        if (permission !== 'granted') return;
        navigator.serviceWorker.ready.then(function(reg) {
          api.get(process.env.REACT_APP_URL + '/push/vapid-public').then(function(keyRes) {
            var vapidPublicKey = keyRes.data.publicKey;
            if (!vapidPublicKey) return;
            var padding = '='.repeat((4 - vapidPublicKey.length % 4) % 4);
            var base64 = (vapidPublicKey + padding).replace(/-/g, '+').replace(/_/g, '/');
            var rawData = atob(base64);
            var outputArray = new Uint8Array(rawData.length);
            for (var i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);

            // Unsubscribe any existing push subscription first (handles stale/expired subs)
            reg.pushManager.getSubscription().then(function(existingSub) {
              var proceed = existingSub ? existingSub.unsubscribe() : Promise.resolve();
              return proceed.then(function() {
                return reg.pushManager.subscribe({
                  userVisibleOnly: true,
                  applicationServerKey: outputArray,
                });
              });
            }).then(function(subscription) {
              api.post(process.env.REACT_APP_URL + '/push/subscribe', {
                username: username,
                subscription: subscription.toJSON(),
              }).catch(function() {});
            }).catch(function(err) {
              console.warn('Push subscription failed:', err.message);
            });
          }).catch(function() {});
        });
      }).catch(function() {});
    }

    subscribePush();

    // Clear badge when app is focused
    function clearBadge() {
      if (!document.hidden && navigator.clearAppBadge) {
        navigator.clearAppBadge().catch(function() {});
      }
    }
    document.addEventListener('visibilitychange', clearBadge);
    return function() { document.removeEventListener('visibilitychange', clearBadge); };
  }, [profile]);

  // Refresh notifications when tab is selected
  useEffect(function() {
    if (bottomTab !== 'notifications') return;
    if (!profile || !profile.patientCard) return;
    setNotifLoading(true);
    api.get(process.env.REACT_APP_URL + '/notifications/patient/' + profile.patientCard)
      .then(function(r) { setNotifications(r.data || []); })
      .catch(function() { setNotifications([]); })
      .finally(function() { setNotifLoading(false); });
  }, [bottomTab, profile]);

  const handleLogout = function() {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('lu');
    sessionStorage.removeItem('saadhakProfile');
    localStorage.removeItem('auth');
    navigate('/login', { replace: true });
  };

  const fetchAllData = async function(cardId) {
    setDataReady(false);
    const [billRes, sessionRes, profileRes, videoRes, plRes, wpRes] = await Promise.allSettled([
      api.get(process.env.REACT_APP_URL + '/bills/' + cardId),
      api.get(process.env.REACT_APP_URL + '/appointments/type/Therapy/patient/' + cardId),
      api.get(process.env.REACT_APP_URL + '/patients/profiles/' + cardId),
      api.get(process.env.REACT_APP_URL + '/resource-videos/patient/' + cardId),
      api.get(process.env.REACT_APP_URL + '/youtube/playlists/patient/' + cardId),
      api.get(process.env.REACT_APP_URL + '/resource-videos/watch-progress/' + cardId),
    ]);
    if (billRes.status    === 'fulfilled' && Array.isArray(billRes.value.data))    setBills(billRes.value.data);
    if (sessionRes.status === 'fulfilled' && Array.isArray(sessionRes.value.data)) setSessions(sessionRes.value.data);
    if (profileRes.status === 'fulfilled' && profileRes.value?.data)               setPatientData(profileRes.value.data);
    if (videoRes.status   === 'fulfilled' && Array.isArray(videoRes.value.data))    setVideos(videoRes.value.data);
    if (plRes.status      === 'fulfilled' && Array.isArray(plRes.value.data))       setAssignedPlaylists(plRes.value.data);
    if (wpRes.status      === 'fulfilled' && Array.isArray(wpRes.value.data)) {
      var wpMap = {};
      wpRes.value.data.forEach(function(p) { wpMap[p.videoId] = p; });
      setWatchProgressMap(wpMap);
    }
    setDataReady(true);
  };

  if (!isLoggedIn) return <Navigate to="/" replace />;

  // ── Derived values ──────────────────────────────────────────────────────────
  const totalSum    = bills.reduce(function(a, b) { return a + b.total;    }, 0);
  const discountSum = bills.reduce(function(a, b) { return a + b.discount; }, 0);
  const paidSum     = bills.reduce(function(a, b) { return a + b.paid;     }, 0);
  const givenBackSum= bills.reduce(function(a, b) { return a + b.givenback;}, 0);
  // True outstanding = max of (totalSoFar - totalPaidSoFar) vs per-org balance sum, + unbilled session fees
  const formalBalance      = (patientData?.previousbalancedetails || []).reduce(function(s, d) { return s + Number(d.balance || 0); }, 0);
  const billedOutstanding  = (patientData?.totalSoFar || 0) - (patientData?.totalPaidSoFar || 0);
  const unbilledSessionFees= Math.max(0, (patientData?.overallVisitCharges || 0) - (patientData?.totalSoFar || 0));
  const balanceSum         = Math.max(formalBalance, billedOutstanding) + unbilledSessionFees;
  const d                  = bills.length ? accumulateData(bills) : null;

  // ── Session-level payment allocation (FIFO: oldest sessions deducted first) ──
  // paidSum = total amount ever paid by patient across all bills (advance pool)
  var _completedSorted = sessions
    .filter(function(s) { return s.status === 'Completed' && (s.visitCharges || 0) > 0; })
    .slice()
    .sort(function(a, b) { return new Date(a.date || a.start) - new Date(b.date || b.start); });

  var _running = paidSum;
  var _allocMap = {};
  _completedSorted.forEach(function(s) {
    var cost = s.visitCharges || 0;
    _allocMap[s._id] = { paid: _running >= cost };
    _running -= cost;
  });
  // _running > 0 → advance still remaining; _running < 0 → patient owes
  var sessionCredit = _running;

  var _lastPaid = null;
  for (var _pi = _completedSorted.length - 1; _pi >= 0; _pi--) {
    if (_allocMap[_completedSorted[_pi]._id].paid) { _lastPaid = _completedSorted[_pi]; break; }
  }
  var firstPending   = _completedSorted.find(function(s) { return !_allocMap[s._id].paid; });
  var pendingCount   = _completedSorted.filter(function(s) { return !_allocMap[s._id].paid; }).length;
  var coverageEndDt  = _lastPaid    ? new Date(_lastPaid.date    || _lastPaid.start)    : null;
  var pendingStartDt = firstPending ? new Date(firstPending.date || firstPending.start) : null;

  const thisMonth  = getMonthCount(sessions, 0);
  const lastMonth  = getMonthCount(sessions, -1);
  const nextSess   = getNextSession(sessions);
  const weekDays   = getLast7Days(sessions);
  const activeDays = weekDays.filter(function(day) { return day.active; }).length;

  // ── Table columns ────────────────────────────────────────────────────────────
  const billColumns = [
    { name: 'Date',     selector: function(r) { return new Date(r.invoicedate); }, format: function(r) { return new Date(r.invoicedate).toLocaleDateString('en-GB'); }, sortable: true, width: '108px' },
    { name: 'Time',     selector: function(r) { return new Date(r.invoicedate).toLocaleTimeString('en-US', { hour12: true }); }, width: '98px' },
    { name: 'Total',    selector: function(r) { return r.total; },    sortable: true, right: true, width: '90px',
      cell: function(r) { return <span className="sd-amt-gold">₹{r.total}</span>; } },
    { name: 'Discount', selector: function(r) { return r.discount; }, sortable: true, width: '90px' },
    { name: 'Received', selector: function(r) { return r.paid; },     sortable: true, width: '95px',
      cell: function(r) { return <span>₹{r.paid}</span>; } },
    { name: 'Balance',  selector: function(r) { return r.balance; },  sortable: true, right: true, width: '95px',
      cell: function(r) {
        return r.balance > 0 ? <span className="sd-chip-due">₹{r.balance}</span>
             : r.balance < 0 ? <span className="sd-chip-credit">₹{r.balance}</span>
             : <span className="sd-chip-nil">Settled</span>;
      }},
    { name: 'Org', selector: function(r) { return r.billorg; }, sortable: true },
  ];

  const sessionColumns = [
    { name: 'Date',      selector: function(r) { return new Date(r.date); }, format: function(r) { return new Date(r.date).toLocaleDateString('en-GB'); }, sortable: true, width: '108px' },
    { name: 'Start',     selector: function(r) { return new Date(r.start).toLocaleTimeString('en-US', { hour12: true }); }, width: '100px' },
    { name: 'Therapist', selector: function(r) { return r.leadTherapist; }, sortable: true },
    { name: 'Status',    selector: function(r) { return r.status; }, sortable: true, width: '105px',
      cell: function(r) { return <span className={r.status === 'Completed' ? 'sd-chip-ok' : 'sd-chip-nil'}>{r.status}</span>; } },
    { name: 'Charges',   selector: function(r) { return r.visitCharges; }, sortable: true, right: true, width: '85px',
      cell: function(r) { return <span className="sd-amt-gold">₹{r.visitCharges || 0}</span>; } },
    { name: 'Payment',   sortable: false, right: true, width: '115px',
      cell: function(r) {
        if (r.status !== 'Completed' || !(r.visitCharges > 0)) {
          return <span style={{color:'rgba(237,233,224,0.25)',fontSize:11}}>—</span>;
        }
        var a = _allocMap[r._id];
        if (!a) return <span style={{color:'rgba(237,233,224,0.25)',fontSize:11}}>—</span>;
        return a.paid
          ? <span style={{color:'#4ade80', fontWeight:700, fontSize:12}}>✓ Paid</span>
          : <span style={{color:'#f87171', fontWeight:700, fontSize:12}}>✗ ₹{(r.visitCharges||0).toLocaleString()} due</span>;
      }
    },
  ];

  const TABS = [
    { id: 'feed',     label: '▶ Feed'    },
   // { id: 'overview', label: 'Overview'  },
   // { id: 'sessions', label: 'Sessions'  },
   // { id: 'bills',    label: 'Bills'     },
  ];
  const tabIdx = TABS.findIndex(function(t) { return t.id === activeTab; });

  // ── Playlist filter helpers ──
  var activePlaylistName = '';
  if (playlistFilter) {
    var pl = (assignedPlaylists || []).find(function(p) { return p.playlistId === playlistFilter; });
    activePlaylistName = pl ? (pl.title || 'Playlist') : 'Playlist';
  }

  // ── Search results ──
  var searchResults = [];
  if (searchQuery.trim().length > 1) {
    var q = searchQuery.trim().toLowerCase();
    searchResults = (videos || []).filter(function(v) {
      return ((v.title || '') + ' ' + (v.description || '')).toLowerCase().includes(q);
    });
  }

  return (
    <div className="sd-root">

      {/* ── Toast notification ── */}
      {sdToast && (
        <div style={{
          position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          zIndex: 10000, minWidth: 260, maxWidth: '88vw',
          background: sdToast.type === 'error' ? '#fef2f2' : sdToast.type === 'success' ? '#f0fdf4' : '#eff6ff',
          color: sdToast.type === 'error' ? '#b91c1c' : sdToast.type === 'success' ? '#15803d' : '#1e40af',
          border: '1.5px solid ' + (sdToast.type === 'error' ? '#fca5a5' : sdToast.type === 'success' ? '#86efac' : '#93c5fd'),
          borderRadius: 14, padding: '13px 18px', fontSize: 14, fontWeight: 600,
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: 10,
          animation: 'popIn 0.3s ease',
        }}>
          <span style={{ fontSize: 18 }}>{sdToast.type === 'error' ? '\u274C' : sdToast.type === 'success' ? '\u2705' : '\u2139\uFE0F'}</span>
          <span style={{ flex: 1 }}>{sdToast.msg}</span>
          <button onClick={function() { setSdToast(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'inherit', padding: 0 }}>{'\u2715'}</button>
        </div>
      )}

      {/* ══════════ MOBILE TOP BAR (hidden on desktop) ══════════ */}
      <div className="sd-top-bar">
        <img src={logo} alt="Logo" style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'contain' }} />
        <div style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 14, color: '#1a1d23' }}>
          {bottomTab === 'home' && !playlistFilter && 'Your Feed'}
          {bottomTab === 'home' && playlistFilter && activePlaylistName}
          {bottomTab === 'search' && 'Search'}
          {bottomTab === 'playlists' && 'My Playlists'}
          {bottomTab === 'notifications' && 'Notifications'}
          {bottomTab === 'profile' && 'Profile'}
        </div>
        {playlistFilter && bottomTab === 'home' ? (
          <button
            onClick={function() { setPlaylistFilter(null); }}
            style={{
              background: 'rgba(108,92,231,0.1)', border: '1px solid rgba(108,92,231,0.3)',
              borderRadius: 14, padding: '3px 10px', fontSize: 11, fontWeight: 600,
              color: '#6C5CE7', cursor: 'pointer',
            }}
          >Clear</button>
        ) : (
          <div style={{ width: 28 }} />
        )}
      </div>

      {/* ── Header (desktop only — hidden on mobile via CSS) ── */}
      <header className="sd-header">
        <div className="sd-header-left">
          <img src={logo} alt="Logo" className="sd-header-logo" />
          <div className="sd-header-brand">
            <span className="sd-brand-name">Saadhak Portal</span>
            <span className="sd-brand-sub">Medical Yoga Therapy Centre</span>
          </div>
        </div>
        <div className="sd-header-right">
          <span className="sd-header-greet">🙏 {profile.name}</span>
          <button className="sd-logout-btn" onClick={handleLogout}>Logout</button>
        </div>
      </header>

      {/* ── Profile Hero Banner ── */}
      <div className="sd-hero" style={{ backgroundImage: 'url(' + bgProfile + ')' }}>
        <div className="sd-hero-overlay">

          {/* ─ Top row: avatar + name/meta + next-session badge ─ */}
          <div className="sd-hero-top">
            <div className="sd-hero-avatar">🧘</div>
            <div className="sd-hero-info">
              <h1 className="sd-hero-name">Namaste, {profile.name} Ji</h1>
              <p className="sd-hero-meta">
                {profile.patientCard}&nbsp;·&nbsp;{profile.number}
                {dataReady && balanceSum > 0 && (
                  <>
                    <span className="sd-hero-due">&nbsp;·&nbsp;⚠ ₹{Math.abs(balanceSum).toLocaleString()} due</span>
                    <button
                      type="button"
                      onClick={function() { setShowPayQR(true); }}
                      style={{
                        marginLeft: 8, padding: '2px 10px', fontSize: 11, fontWeight: 700,
                        background: '#C9A84C', color: '#000', border: 'none',
                        borderRadius: 10, cursor: 'pointer', verticalAlign: 'middle',
                      }}
                    >Pay Now</button>
                  </>
                )}
              </p>
              {profile.conditions && profile.conditions.length > 0 && (
                <div className="sd-hero-tags">
                  {profile.conditions.map(function(c, i) { return <span key={i} className="sd-hero-tag">{c}</span>; })}
                </div>
              )}
            </div>
            {nextSess && (
              <div className="sd-hero-next">
                <span className="sd-next-label">Next Session</span>
                <span className="sd-next-days-hero">{daysUntil(nextSess.date)}<small>d</small></span>
                <span className="sd-next-date-hero">
                  {new Date(nextSess.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </span>
              </div>
            )}
          </div>

          {/* ─ Quick Actions row ─ */}
          <div className="sd-hero-actions">
            <button className="sd-action-btn" title="Book a therapy session" onClick={function() { setShowBooking(true); }}>
              <span className="sd-action-icon">📅</span>
              <span>Book&nbsp;Session</span>
            </button>

            <button className="sd-action-btn" title="Buy yoga props and accessories" onClick={function() {
              window.open('https://www.shop.medicalyoga.in', '_blank', 'noopener,noreferrer');
            }}>
              <span className="sd-action-icon">🛍</span>
              <span>Shop&nbsp;Props</span>
            </button>
            {/*
            <button className="sd-action-btn" title="View upcoming courses" onClick={function() { setShowCourses(true); }}>
              <span className="sd-action-icon">🎓</span>
              <span>Courses</span>
            </button>

            <button className="sd-action-btn" title="View billing and payments" onClick={function() { setActiveTab('bills'); }}>
              <span className="sd-action-icon">💰</span>
              <span>My&nbsp;Bills</span>
            </button>
            */}
          </div>

        </div>
      </div>

      {/* ── Tab Bar ── */}
      <nav className="sd-tab-bar">
        {TABS.map(function(t) {
          return (
            <button
              key={t.id}
              className={'sd-tab' + (activeTab === t.id ? ' sd-tab-active' : '')}
              onClick={function() { setActiveTab(t.id); }}
            >
              {t.label}
            </button>
          );
        })}
        <div className="sd-tab-ink" style={{ left: (tabIdx * 25) + '%', width: '25%' }} />
      </nav>

      {/* ── Main Content ── */}
      <main className="sd-main">

        {/* ═══════ MOBILE-ONLY CONTENT (controlled by bottomTab) ═══════ */}
        <div className="sd-mobile-content">
          {!dataReady && (
            <div className="sd-loading">
              <div className="sd-spinner" />
              <span>Loading your wellness data…</span>
            </div>
          )}
          {dataReady && bottomTab === 'home' && (
            <>
              {/* Compact hero for mobile home */}
              <div style={{ padding: '16px 14px 8px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(108,92,231,0.15), rgba(0,184,148,0.15))',
                  border: '2px solid rgba(108,92,231,0.25)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0,
                }}>🧘</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 16, color: '#1a1d23', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {profile.name}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>
                    {profile.patientCard}
                    {balanceSum > 0 && (
                      <span style={{ color: '#ef4444', fontWeight: 700 }}> · ₹{Math.abs(balanceSum).toLocaleString()} due</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={function() { setShowBooking(true); }}
                  style={{
                    background: '#6C5CE7', color: '#fff', border: 'none', borderRadius: 20,
                    padding: '8px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer', flexShrink: 0,
                  }}
                >Book Session</button>
              </div>
              {isMobile && <HomeFeedSection assignedVideos={videos} assignedPlaylists={assignedPlaylists} plVidsMap={plVidsMap} plVidsLoading={plVidsLoading} playlistFilter={playlistFilter} watchProgressMap={watchProgressMap} setWatchProgressMap={setWatchProgressMap} patientCard={profile?.patientCard || ''} />}
              {profile && <FilesFeedSection patientCard={profile.patientCard} />}
            </>
          )}

          {dataReady && bottomTab === 'search' && (
            <div style={{ padding: '12px 14px' }}>
              <div style={{ position: 'relative', marginBottom: 16 }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={function(e) { setSearchQuery(e.target.value); }}
                  placeholder="Search videos..."
                  style={{
                    width: '100%', padding: '12px 14px 12px 40px', borderRadius: 12,
                    border: '1.5px solid #e5e7eb', fontSize: 14, background: '#f9fafb',
                    outline: 'none', boxSizing: 'border-box',
                  }}
                />
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"
                  style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}>
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </div>
              {searchQuery.trim().length < 2 && (
                <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40, fontSize: 13 }}>
                  Type at least 2 characters to search your assigned videos
                </div>
              )}
              {searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40, fontSize: 13 }}>
                  No videos found matching "{searchQuery}"
                </div>
              )}
              {searchResults.map(function(v) {
                var ytId = v.youtubeId || getYoutubeId(v.url || '');
                return (
                  <div key={v._id || ytId} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
                    borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                  }}
                  onClick={function() {
                    setPlaylistFilter(null);
                    setBottomTab('home');
                    setSearchQuery('');
                  }}>
                    <div style={{
                      width: 80, height: 50, borderRadius: 8, overflow: 'hidden', background: '#000', flexShrink: 0,
                    }}>
                      {ytId && <img src={'https://img.youtube.com/vi/' + ytId + '/mqdefault.jpg'} alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1d23', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {v.title}
                      </div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                        {v.source === 'drive' ? 'Private video' : 'YouTube'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {dataReady && bottomTab === 'playlists' && (
            <PlaylistsTabSection assignedVideos={videos} assignedPlaylists={assignedPlaylists} plVidsMap={plVidsMap} plVidsLoading={plVidsLoading} watchProgressMap={watchProgressMap} setWatchProgressMap={setWatchProgressMap} patientCard={profile?.patientCard || ''} playlistFilter={playlistFilter} setPlaylistFilter={setPlaylistFilter} setBottomTab={setBottomTab} videos={videos} />
          )}

          {dataReady && bottomTab === 'notifications' && (
            <div style={{ padding: '12px 14px' }}>
              {notifLoading && (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div className="sd-spinner" style={{ margin: '0 auto 12px' }} />
                  <span style={{ color: '#9ca3af', fontSize: 13 }}>Loading...</span>
                </div>
              )}
              {!notifLoading && notifications.length === 0 && (
                <div style={{ textAlign: 'center', padding: '48px 20px' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom: 12 }}>
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                  </svg>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#6b7280', marginBottom: 4 }}>No notifications</div>
                  <div style={{ fontSize: 13, color: '#9ca3af' }}>You're all caught up!</div>
                </div>
              )}
              {!notifLoading && notifications.map(function(n) {
                var notifColors = {
                  PATIENT_VIDEO_ASSIGNED: '#E17055', PATIENT_PLAYLIST_ASSIGNED: '#E17055',
                  PATIENT_APPT_SCHEDULED: '#0984e3', PATIENT_APPT_COMPLETED: '#00B894',
                  PATIENT_APPT_MISSED: '#d63031', PATIENT_APPT_TOMORROW: '#6C5CE7',
                  PATIENT_APPT_SOON: '#e84393',
                  PATIENT_PAYMENT_RECEIVED: '#00B894', PATIENT_BALANCE_DUE: '#fdcb6e',
                  PATIENT_REPORT_UPLOADED: '#0984e3', PATIENT_RENEWAL: '#E17055',
                  PATIENT_WELCOME: '#6C5CE7',
                };
                var color = notifColors[n.type] || '#6C5CE7';
                var isUnread = !n.read;
                return (
                  <div key={n._id} onClick={function() {
                    if (isUnread) {
                      api.patch(process.env.REACT_APP_URL + '/notifications/patient/' + n._id + '/read').catch(function() {});
                      setNotifications(function(prev) {
                        return prev.map(function(x) { return x._id === n._id ? Object.assign({}, x, { read: true }) : x; });
                      });
                    }
                  }} style={{
                    padding: '14px 0', borderBottom: '1px solid #f3f4f6',
                    display: 'flex', gap: 12, alignItems: 'flex-start',
                    cursor: isUnread ? 'pointer' : 'default',
                    background: isUnread ? 'rgba(108,92,231,0.04)' : 'transparent',
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                      background: color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 16,
                    }}>
                      {(n.title || '').charAt(0) || '🔔'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: isUnread ? 700 : 600, fontSize: 13, color: '#1a1d23', marginBottom: 2 }}>{n.title || 'Notification'}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>{n.body || n.message || ''}</div>
                      <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
                        {n.createdAt ? new Date(n.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </div>
                    </div>
                    {isUnread && <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0, marginTop: 6 }} />}
                  </div>
                );
              })}
            </div>
          )}

          {dataReady && bottomTab === 'profile' && (
            <div style={{ padding: '16px 14px' }}>
              {/* Profile card */}
              <div style={{
                background: '#fff', borderRadius: 18, padding: '24px 20px', textAlign: 'center',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 16,
                borderTop: '3px solid #6C5CE7',
              }}>
                <div style={{
                  width: 72, height: 72, borderRadius: '50%', margin: '0 auto 12px',
                  background: 'linear-gradient(135deg, rgba(108,92,231,0.12), rgba(0,184,148,0.12))',
                  border: '3px solid rgba(108,92,231,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32,
                }}>🧘</div>
                <div style={{ fontWeight: 800, fontSize: 20, color: '#1a1d23', marginBottom: 4 }}>
                  {profile.name}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                  {profile.patientCard} · {profile.number}
                </div>
                {profile.conditions && profile.conditions.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 6, marginTop: 8 }}>
                    {profile.conditions.map(function(c, i) {
                      return <span key={i} style={{
                        padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                        background: 'rgba(108,92,231,0.08)', color: '#6C5CE7',
                        border: '1px solid rgba(108,92,231,0.2)',
                      }}>{c}</span>;
                    })}
                  </div>
                )}
              </div>

              {/* Next session */}
              {nextSess && (
                <div style={{
                  background: '#fff', borderRadius: 14, padding: '16px 18px', marginBottom: 12,
                  boxShadow: '0 1px 6px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: 14,
                  border: '1px solid #e5e7eb',
                }}>
                  <div style={{ fontSize: 28 }}>🗓</div>
                  <div>
                    <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Next Session</div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1d23' }}>
                      {new Date(nextSess.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                      {nextSess.leadTherapist && <span style={{ color: '#6b7280', fontWeight: 500 }}> · {nextSess.leadTherapist}</span>}
                    </div>
                  </div>
                  <div style={{ marginLeft: 'auto', fontWeight: 800, fontSize: 18, color: '#6C5CE7' }}>
                    {daysUntil(nextSess.date)}<span style={{ fontSize: 11, fontWeight: 500 }}>d</span>
                  </div>
                </div>
              )}

              {/* Balance */}
              {balanceSum > 0 && (
                <div style={{
                  background: '#fff', borderRadius: 14, padding: '16px 18px', marginBottom: 12,
                  boxShadow: '0 1px 6px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: 14,
                  border: '1px solid rgba(239,68,68,0.2)',
                }}>
                  <div style={{ fontSize: 22 }}>💳</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Balance Due</div>
                    <div style={{ fontWeight: 800, fontSize: 18, color: '#ef4444' }}>₹{Math.abs(balanceSum).toLocaleString()}</div>
                  </div>
                  <button onClick={function() { setShowPayQR(true); }}
                    style={{
                      background: '#C9A84C', color: '#000', border: 'none', borderRadius: 10,
                      padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    }}>Pay Now</button>
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <button onClick={function() { setShowBooking(true); }}
                  style={{
                    flex: 1, padding: '12px 0', background: '#6C5CE7', color: '#fff',
                    border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                  }}>📅 Book Session</button>
                <button onClick={function() { window.open('https://www.shop.medicalyoga.in', '_blank', 'noopener,noreferrer'); }}
                  style={{
                    flex: 1, padding: '12px 0', background: '#00B894', color: '#fff',
                    border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                  }}>🛍 Shop Props</button>
              </div>

              {/* Contact Us */}
              <button onClick={function() { setShowContactUs(true); }}
                style={{
                  width: '100%', padding: '12px 0', background: '#fff', color: '#1e3a5f',
                  border: '1.5px solid #2563eb', borderRadius: 12, fontWeight: 700, fontSize: 13,
                  cursor: 'pointer', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                Contact Us
              </button>

              {/* Change Password link */}
              <button onClick={function() { navigate('/ChangePassword'); }}
                style={{
                  width: '100%', padding: '12px 0', background: '#f9fafb', color: '#374151',
                  border: '1.5px solid #e5e7eb', borderRadius: 12, fontWeight: 600, fontSize: 13,
                  cursor: 'pointer', marginBottom: 10,
                }}>Change Password</button>

              {/* Logout */}
              <button onClick={handleLogout}
                style={{
                  width: '100%', padding: '12px 0', background: '#fef2f2', color: '#dc2626',
                  border: '1.5px solid rgba(220,38,38,0.2)', borderRadius: 12, fontWeight: 700,
                  fontSize: 13, cursor: 'pointer',
                }}>Logout</button>
            </div>
          )}
        </div>

        {/* ═══════ DESKTOP CONTENT (hidden on mobile via CSS) ═══════ */}
        <div className="sd-desktop-content">
        {!dataReady && (
          <div className="sd-loading">
            <div className="sd-spinner" />
            <span>Loading your wellness data…</span>
          </div>
        )}

        {dataReady && (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.25 }}
            >

              {/* ══════════════════════════ OVERVIEW ══════════════════════════ */}
              {activeTab === 'overview' && (
                <div className="sd-section">

                  {/* KPI Hero Row — 3 glass cards */}
                  <div className="sd-kpi-row">

                    {/* Sessions ring card — ring shows this-month progress toward 8-session target */}
                    <div className="sd-glass-card sd-kpi-card">
                      <div className="sd-card-shine" />
                      <p className="sd-card-title">Sessions</p>
                      <ProgressRing
                        value={thisMonth}
                        maxValue={8}
                        size={110}
                        strokeWidth={9}
                        color="#6C5CE7"
                        centerLabel={thisMonth + '/8'}
                        centerSub="this month"
                      />
                      <div className="sd-kpi-meta">
                        <div className="sd-kpi-meta-item">
                          <span className="sd-kpi-meta-val">{thisMonth}</span>
                          <span className="sd-kpi-meta-lbl">this month</span>
                        </div>
                        <div className="sd-kpi-meta-div" />
                        <div className="sd-kpi-meta-item">
                          <span className="sd-kpi-meta-val">{lastMonth}</span>
                          <span className="sd-kpi-meta-lbl">last month</span>
                        </div>
                      </div>
                    </div>

                    {/* Next session card */}
                    <div className="sd-glass-card sd-kpi-card">
                      <div className="sd-card-shine" />
                      <p className="sd-card-title">Next Session</p>
                      {nextSess ? (
                        <div className="sd-next-body">
                          <div className="sd-next-icon-big">🗓</div>
                          <div className="sd-next-days-big">{daysUntil(nextSess.date)}</div>
                          <div className="sd-next-days-lbl">days away</div>
                          <div className="sd-next-info-row">
                            {new Date(nextSess.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                          </div>
                          {nextSess.leadTherapist && (
                            <div className="sd-next-therapist">with {nextSess.leadTherapist}</div>
                          )}
                        </div>
                      ) : (
                        <div className="sd-next-body sd-next-none">
                          <div className="sd-next-icon-big">✅</div>
                          <p>No upcoming session<br />scheduled</p>
                        </div>
                      )}
                    </div>

                    {/* Balance card */}
                    <div className={'sd-glass-card sd-kpi-card sd-bal-card' + (balanceSum > 0 ? ' sd-bal-overdue' : '')}>
                      <div className="sd-card-shine" />
                      <p className="sd-card-title">Balance</p>
                      <div className="sd-bal-amount" style={{ color: balanceSum > 0 ? '#F06A6A' : '#4ADE80' }}>
                        ₹{Math.abs(balanceSum).toLocaleString()}
                      </div>
                      <div className="sd-bal-tag" style={{
                        background: balanceSum > 0 ? 'rgba(240,106,106,0.15)' : 'rgba(74,222,128,0.15)',
                        color:      balanceSum > 0 ? '#F06A6A' : '#4ADE80',
                      }}>
                        {balanceSum > 0 ? '⚠ Amount Due' : balanceSum < 0 ? '↩ Credit Balance' : '✓ Fully Settled'}
                      </div>
                      <div className="sd-bal-rows">
                        <div className="sd-bal-row"><span>Total Billed</span><strong>₹{totalSum.toLocaleString()}</strong></div>
                        <div className="sd-bal-row"><span>Received</span><strong>₹{paidSum.toLocaleString()}</strong></div>
                        <div className="sd-bal-row"><span>Discount</span><strong>₹{discountSum.toLocaleString()}</strong></div>
                        <div className="sd-bal-row"><span>Given Back</span><strong>₹{givenBackSum.toLocaleString()}</strong></div>
                        {unbilledSessionFees > 0 && (
                          <div className="sd-bal-row"><span>Unbilled Sessions</span><strong style={{color:'#F06A6A'}}>₹{unbilledSessionFees.toLocaleString()}</strong></div>
                        )}
                      </div>
                    </div>

                  </div>{/* /kpi-row */}

                  {/* 7-Day Activity Streak */}
                  <div className="sd-glass-card sd-streak-card">
                    <div className="sd-card-shine" />
                    <div className="sd-streak-head">
                      <p className="sd-card-title" style={{ marginBottom: 0 }}>This Week's Activity</p>
                      <span className="sd-streak-sub">{activeDays} of 7 days</span>
                    </div>
                    <div className="sd-streak-dots">
                      {weekDays.map(function(day, i) {
                        return (
                          <div key={i} className="sd-dot-col">
                            <div className={'sd-dot' + (day.active ? ' sd-dot-on' : '') + (day.today ? ' sd-dot-today' : '')} />
                            <span className="sd-dot-lbl">{day.label}</span>
                          </div>
                        );
                      })}
                    </div>
                    <p className="sd-streak-msg">
                      {activeDays > 0
                        ? '🔥 ' + activeDays + ' session' + (activeDays > 1 ? 's' : '') + ' this week — consistent effort leads to healing.'
                        : '🙏 Start your healing journey — attend your next session.'}
                    </p>
                  </div>

                  {/* Visit Frequency — Area Chart */}
                  {d && d.lineData.length > 0 && (
                    <div className="sd-glass-card sd-chart-wide">
                      <div className="sd-card-shine" />
                      <p className="sd-card-title">Visit Frequency Over Time</p>
                      <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={d.lineData} margin={{ top: 8, right: 12, left: -24, bottom: 0 }}>
                          <defs>
                            <linearGradient id="visitGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%"  stopColor="#6C5CE7" stopOpacity={0.30} />
                              <stop offset="95%" stopColor="#6C5CE7" stopOpacity={0}    />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                          <XAxis dataKey="date" tick={{ fill: 'rgba(237,233,224,0.4)', fontSize: 10 }} />
                          <YAxis allowDecimals={false} tick={{ fill: 'rgba(237,233,224,0.4)', fontSize: 10 }} />
                          <Tooltip contentStyle={ttStyle} />
                          <Area type="monotone" dataKey="visits" stroke="#6C5CE7" strokeWidth={2.5}
                            fill="url(#visitGrad)" dot={{ r: 4, fill: '#6C5CE7', strokeWidth: 0 }}
                            activeDot={{ r: 6, fill: '#a29bfe' }} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Charts Row — Pie + Bar */}
                  {d && (
                    <div className="sd-charts-row">
                      <div className="sd-glass-card sd-chart-half">
                        <div className="sd-card-shine" />
                        <p className="sd-card-title">Payment Modes</p>
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie data={d.pieData} dataKey="value" nameKey="name"
                              cx="50%" cy="50%" outerRadius={70} innerRadius={28} paddingAngle={3} label>
                              {d.pieData.map(function(_, idx) {
                                return <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />;
                              })}
                            </Pie>
                            <Tooltip contentStyle={ttStyle} />
                            <Legend wrapperStyle={{ color: 'rgba(237,233,224,0.6)', fontSize: 12 }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="sd-glass-card sd-chart-half">
                        <div className="sd-card-shine" />
                        <p className="sd-card-title">Spend by Organisation</p>
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={d.barOrgData} margin={{ top: 8, right: 10, left: -24, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                            <XAxis dataKey="org" tick={{ fill: 'rgba(237,233,224,0.4)', fontSize: 10 }} />
                            <YAxis tick={{ fill: 'rgba(237,233,224,0.4)', fontSize: 10 }} />
                            <Tooltip formatter={function(v) { return '₹' + v; }} contentStyle={ttStyle} />
                            <Bar dataKey="spend" fill="#6C5CE7" radius={[6, 6, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* Previous balance details */}
                  {patientData?.previousbalancedetails && patientData.previousbalancedetails.length > 0 && (
                    <div className="sd-glass-card">
                      <div className="sd-card-shine" />
                      <p className="sd-card-title">Previous Balance Details</p>
                      <div className="sd-prev-list">
                        {patientData.previousbalancedetails.map(function(b, i) {
                          return (
                            <div key={i} className="sd-prev-item">
                              <span>{b.Org}</span>
                              <span style={{ color: b.balance > 0 ? '#F06A6A' : '#4ADE80', fontWeight: 600 }}>
                                ₹{b.balance}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {!d && <p className="sd-empty">No billing records found yet.</p>}
                </div>
              )}

              {/* ══════════════════════════ SESSIONS ══════════════════════════ */}
              {activeTab === 'sessions' && (
                <div className="sd-section">

                  {/* ── Payment allocation summary ── */}
                  <div style={{display:'flex', gap:10, marginBottom:12, flexWrap:'wrap'}}>
                    <div style={{
                      background: sessionCredit >= 0 ? 'rgba(74,222,128,0.12)' : 'rgba(248,113,113,0.10)',
                      border: '1.5px solid ' + (sessionCredit >= 0 ? '#4ade80' : '#f87171'),
                      borderRadius:10, padding:'10px 16px', flex:1, minWidth:140,
                    }}>
                      <div style={{fontSize:11, color:'rgba(237,233,224,0.5)', marginBottom:2}}>
                        {sessionCredit >= 0 ? 'Advance Remaining' : 'Outstanding Amount'}
                      </div>
                      <div style={{fontSize:22, fontWeight:800, color: sessionCredit >= 0 ? '#4ade80' : '#f87171'}}>
                        {sessionCredit >= 0 ? '+' : '-'}₹{Math.abs(sessionCredit).toLocaleString()}
                      </div>
                    </div>

                    <div style={{background:'rgba(201,168,76,0.10)', border:'1.5px solid rgba(201,168,76,0.35)', borderRadius:10, padding:'10px 16px', flex:1, minWidth:140}}>
                      <div style={{fontSize:11, color:'rgba(237,233,224,0.5)', marginBottom:2}}>Total Paid</div>
                      <div style={{fontSize:18, fontWeight:700, color:'#C9A84C'}}>₹{paidSum.toLocaleString()}</div>
                      <div style={{fontSize:11, color:'rgba(237,233,224,0.35)'}}>{bills.length} bill{bills.length !== 1 ? 's' : ''}</div>
                    </div>

                    {coverageEndDt && (
                      <div style={{background:'rgba(108,92,231,0.12)', border:'1.5px solid rgba(108,92,231,0.4)', borderRadius:10, padding:'10px 16px', flex:1, minWidth:140}}>
                        <div style={{fontSize:11, color:'rgba(237,233,224,0.5)', marginBottom:2}}>Sessions Paid Until</div>
                        <div style={{fontSize:14, fontWeight:700, color:'#a29bfe'}}>
                          {coverageEndDt.toLocaleDateString('en-GB', {day:'numeric', month:'short', year:'numeric'})}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Pending sessions notice ── */}
                  {pendingCount > 0 && (
                    <div style={{
                      background:'rgba(248,113,113,0.08)', border:'1.5px solid rgba(248,113,113,0.4)',
                      borderRadius:10, padding:'12px 16px', marginBottom:12,
                      display:'flex', alignItems:'flex-start', gap:10,
                    }}>
                      <span style={{fontSize:18, flexShrink:0, lineHeight:1.3}}>⚠</span>
                      <div>
                        <div style={{fontWeight:700, color:'#f87171', fontSize:13, marginBottom:3}}>
                          {pendingCount} session{pendingCount !== 1 ? 's' : ''} pending payment
                        </div>
                        <div style={{fontSize:12, color:'rgba(237,233,224,0.6)', lineHeight:1.6}}>
                          {pendingStartDt && (
                            <span>Starting from&nbsp;
                              <strong style={{color:'#fca5a5'}}>
                                {pendingStartDt.toLocaleDateString('en-GB', {day:'numeric', month:'short', year:'numeric'})}
                              </strong>.&nbsp;
                            </span>
                          )}
                          Total outstanding:&nbsp;
                          <strong style={{color:'#fca5a5'}}>₹{Math.abs(Math.min(sessionCredit, 0)).toLocaleString()}</strong>.
                          &nbsp;Please contact the centre to settle your account.
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="sd-glass-card sd-table-card">
                    <div className="sd-card-shine" />
                    <p className="sd-card-title">
                      Your Therapy Sessions&nbsp;
                      <span className="sd-count-badge">{sessions.length}</span>
                    </p>
                    <DataTable
                      columns={sessionColumns}
                      data={sessions}
                      customStyles={darkTable}
                      expandableRows
                      expandableRowsComponent={SessionExpandRow}
                      highlightOnHover
                      dense
                      pagination
                      paginationPerPage={15}
                      noDataComponent={<p className="sd-empty">No therapy sessions found.</p>}
                    />
                  </div>
                </div>
              )}

              {/* ══════════════════════════ BILLS ══════════════════════════════ */}
              {activeTab === 'bills' && (
                <div className="sd-section">
                  <div className="sd-glass-card sd-table-card">
                    <div className="sd-card-shine" />
                    <p className="sd-card-title">Medical Yoga Therapy Centre</p>
                    <DataTable
                      columns={billColumns}
                      data={bills.filter(function(b) { return b.billorg === 'MedicalyogaTherapyCentre'; })}
                      customStyles={darkTable}
                      expandableRows
                      expandableRowsComponent={BillExpandRow}
                      highlightOnHover
                      dense
                      pagination
                      paginationPerPage={10}
                      noDataComponent={<p className="sd-empty">No bills found for this centre.</p>}
                    />
                  </div>

                  <div className="sd-glass-card sd-table-card" style={{ marginTop: 16 }}>
                    <div className="sd-card-shine" />
                    <p className="sd-card-title">ReRoot Wellness</p>
                    <DataTable
                      columns={billColumns}
                      data={bills.filter(function(b) { return b.billorg === 'ReRoot'; })}
                      customStyles={darkTable}
                      expandableRows
                      expandableRowsComponent={BillExpandRow}
                      highlightOnHover
                      dense
                      pagination
                      paginationPerPage={10}
                      noDataComponent={<p className="sd-empty">No bills found for ReRoot.</p>}
                    />
                  </div>
                </div>
              )}

              {/* ══════════════════════════ FEED ════════════════════════════════ */}
              {activeTab === 'feed' && (
                <>
                  {!isMobile && <HomeFeedSection assignedVideos={videos} assignedPlaylists={assignedPlaylists} plVidsMap={plVidsMap} plVidsLoading={plVidsLoading} playlistFilter={null} watchProgressMap={watchProgressMap} setWatchProgressMap={setWatchProgressMap} patientCard={profile?.patientCard || ''} />}
                  {profile && <FilesFeedSection patientCard={profile.patientCard} />}
                </>
              )}

              {/* ══════════════════════════ VIDEOS (legacy tab) ═════════════════ */}
              {activeTab === 'videos' && (
                <div className="sd-section">
                  <p className="sd-section-title">Assigned Therapy Videos</p>
                  {videos.length === 0 ? (
                    <p className="sd-empty">No videos assigned yet. Check back after your next session.</p>
                  ) : (
                    <div className="sd-videos-grid">
                      {videos.map(function(v) {
                        return (
                          <motion.div
                            key={v.id}
                            className="sd-glass-card sd-video-card"
                            whileHover={{ scale: 1.012 }}
                            transition={{ duration: 0.18 }}
                          >
                            <div className="sd-card-shine" />
                            <p className="sd-video-title">{v.title}</p>
                            <div className="sd-video-frame">
                              <iframe
                                src={'https://www.youtube.com/embed/' + v.youtubeId 
                                  + '?rel=0&modestbranding=1&controls=1&playsinline=1&fs=0&' 
                                  //+ 'origin=' + window.location.origin
                                  }
                                title={v.title}
                                style={{ border: 'none' }}
                                referrerPolicy="strict-origin-when-cross-origin"
                                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            </div>
                            <p className="sd-video-note">🔒 Private — for your wellness journey only</p>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

            </motion.div>
          </AnimatePresence>
        )}
        </div>{/* /sd-desktop-content */}
      </main>

      {/* ── Footer (desktop only — hidden on mobile via CSS) ── */}
      <footer className="sd-footer">
        <span className="sd-footer-om">ॐ</span>
        © {new Date().getFullYear()} Medical Yoga Therapy Centre · ReRoot Wellness
      </footer>

      {/* ══════════ MOBILE BOTTOM NAV (hidden on desktop) ══════════ */}
      <nav className="sd-bottom-nav">
        <button className={'sd-bnav-item' + (bottomTab === 'home' ? ' sd-bnav-active' : '')}
          onClick={function() { setBottomTab('home'); }}
          style={{ color: bottomTab === 'home' ? '#6C5CE7' : '#9ca3af' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill={bottomTab === 'home' ? '#6C5CE7' : 'none'} stroke={bottomTab === 'home' ? '#6C5CE7' : '#9ca3af'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          <span>Home</span>
        </button>
        <button className={'sd-bnav-item' + (bottomTab === 'search' ? ' sd-bnav-active' : '')}
          onClick={function() { setBottomTab('search'); }}
          style={{ color: bottomTab === 'search' ? '#0984e3' : '#9ca3af' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={bottomTab === 'search' ? '#0984e3' : '#9ca3af'} strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <span>Search</span>
        </button>
        <button className={'sd-bnav-item' + (bottomTab === 'playlists' ? ' sd-bnav-active' : '')}
          onClick={function() { setBottomTab('playlists'); }}
          style={{ color: bottomTab === 'playlists' ? '#00B894' : '#9ca3af' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill={bottomTab === 'playlists' ? '#00B894' : 'none'} stroke={bottomTab === 'playlists' ? '#00B894' : '#9ca3af'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="3"/><polygon points="10,8 16,12 10,16"/>
          </svg>
          <span>Playlists</span>
        </button>
        <button className={'sd-bnav-item' + (bottomTab === 'notifications' ? ' sd-bnav-active' : '')}
          onClick={function() { setBottomTab('notifications'); }}
          style={{ color: bottomTab === 'notifications' ? '#E17055' : '#9ca3af' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill={bottomTab === 'notifications' ? '#E17055' : 'none'} stroke={bottomTab === 'notifications' ? '#E17055' : '#9ca3af'} strokeWidth="2" strokeLinecap="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
          <span>Activity</span>
          {notifications.filter(function(n) { return !n.read; }).length > 0 && (
            <span className="sd-bnav-badge">{notifications.filter(function(n) { return !n.read; }).length > 9 ? '9+' : notifications.filter(function(n) { return !n.read; }).length}</span>
          )}
        </button>
        <button className={'sd-bnav-item' + (bottomTab === 'profile' ? ' sd-bnav-active' : '')}
          onClick={function() { setBottomTab('profile'); }}
          style={{ color: bottomTab === 'profile' ? '#e84393' : '#9ca3af' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill={bottomTab === 'profile' ? '#e84393' : 'none'} stroke={bottomTab === 'profile' ? '#e84393' : '#9ca3af'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
          </svg>
          <span>Profile</span>
        </button>
      </nav>

      {/* ── BOOKING FLOW MODAL ────────────────────────────── */}
      {showBooking && profile && (
        <BookAppointmentFlow
          patientCard={profile.patientCard}
          patientName={profile.name}
          patientPhone={profile.number}
          branch={profile.branch}
          allowBookForOthers={true}
          onClose={function() { setShowBooking(false); }}
          onBooked={function(appt) {
            setShowBooking(false);
            fetchAllData(profile.patientCard);
          }}
          showToast={showSdToast}
        />
      )}

      {/* ── COURSES MODAL ─────────────────────────────────── */}
      {showCourses && <CoursesModal onClose={function() { setShowCourses(false); }} profile={profile} />}

      {/* ── CONTACT US MODAL ───────────────────────────────── */}
      {showContactUs && <ContactUsModal onClose={function() { setShowContactUs(false); }} profile={profile} />}

      {/* ── PWA INSTALL PROMPT ────────────────────────────── */}
      <InstallPrompt />

      {/* ── PAY NOW UPI QR MODAL ──────────────────────────── */}
      {showPayQR && balanceSum > 0 && (
        <div
          onClick={function() { setShowPayQR(false); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)',
            zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            onClick={function(e) { e.stopPropagation(); }}
            style={{
              background: '#fff', borderRadius: 20, padding: '28px 24px',
              maxWidth: 340, width: '100%', textAlign: 'center',
              boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
            }}
          >
            <div style={{ fontSize: 22, marginBottom: 4 }}>💳</div>
            <div style={{ fontWeight: 900, fontSize: 17, color: '#1a1d23', marginBottom: 2 }}>
              Pay Outstanding Amount
            </div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
              Scan with any UPI app to pay
            </div>

            {/* QR Code */}
            <div style={{ display: 'inline-block', padding: 12, background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14 }}>
              <QRCodeCanvas
                value={`upi://pay?pa=9891043444@ptyes&pn=MedicalYogaTherapy&am=${Math.abs(balanceSum)}&tn=Session%20Payment&cu=INR`}
                size={200}
                level="H"
                includeMargin={false}
              />
            </div>

            {/* Amount */}
            <div style={{ marginTop: 16, fontSize: 28, fontWeight: 900, color: '#dc2626' }}>
              ₹{Math.abs(balanceSum).toLocaleString()}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>UPI ID: 9891043444@ptyes</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 20 }}>Medical Yoga Therapy Centre</div>

            <button
              type="button"
              onClick={function() { setShowPayQR(false); }}
              style={{
                width: '100%', padding: '12px 0', background: '#f1f5f9',
                color: '#374151', border: '1px solid #e2e8f0', borderRadius: 12,
                fontWeight: 600, fontSize: 14, cursor: 'pointer',
              }}
            >Close</button>
          </div>
        </div>
      )}


    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   COURSES MODAL
═══════════════════════════════════════════════════════════ */
const COURSES = [
  {
    title: 'Medical Yoga Foundation Course',
    subtitle: 'Level 1 — Beginners',
    icon: '🧘',
    timing: 'Every Saturday & Sunday · 7:00 AM – 9:00 AM',
    venue: 'Medical Yoga Therapy Centre, Sector 14, Gurugram',
    duration: '8 Weeks',
    nextBatch: '17 May 2026',
    fee: 4800,
    seats: 12,
    filled: 8,
    highlights: ['Pain Management Basics', 'Breathing Techniques', 'Posture Correction'],
  },
  {
    title: 'Spine & Back Care Intensive',
    subtitle: 'Targeted Therapy Program',
    icon: '🦴',
    timing: 'Mon, Wed, Fri · 6:30 AM – 7:30 AM',
    venue: 'Medical Yoga Therapy Centre, Sector 14, Gurugram',
    duration: '6 Weeks',
    nextBatch: '4 May 2026',
    fee: 6000,
    seats: 10,
    filled: 10,
    highlights: ['Disc & Spine Protocols', 'Sciatica Relief', 'Core Strengthening'],
  },
  {
    title: 'Stress & Anxiety Relief — Yoga & Meditation',
    subtitle: 'Mind-Body Program',
    icon: '🧠',
    timing: 'Tue & Thu · 7:00 PM – 8:30 PM',
    venue: 'Medical Yoga Therapy Centre, Sector 14, Gurugram',
    duration: '4 Weeks',
    nextBatch: '6 May 2026',
    fee: 3600,
    seats: 15,
    filled: 6,
    highlights: ['Pranayama', 'Yoga Nidra', 'Mindfulness Practices'],
  },
  {
    title: 'Post-Surgery Rehabilitation Yoga',
    subtitle: 'Recovery & Mobility',
    icon: '🏥',
    timing: 'Flexible — Individual Sessions',
    venue: 'Medical Yoga Therapy Centre, Sector 14, Gurugram',
    duration: '12 Weeks',
    nextBatch: 'Ongoing',
    fee: 12000,
    seats: 8,
    filled: 4,
    highlights: ['Custom Recovery Plan', 'Therapist-Supervised', 'Progress Tracking'],
  },
];

function CoursesModal({ onClose, profile }) {
  const [bookingCourse, setBookingCourse] = React.useState(null);
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
        <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.35)' }}>
          <div style={{ background: 'linear-gradient(135deg,#C9A84C,#92400e)', padding: '18px 22px', borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 1 }}>
            <div>
              <div style={{ color: '#fff', fontWeight: 900, fontSize: 17 }}>🎓 Upcoming Courses</div>
              <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11, marginTop: 2 }}>Medical Yoga Therapy Programmes</div>
            </div>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>✕ Close</button>
          </div>
          <div style={{ padding: '18px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {COURSES.map(function(c, i) {
              const avail = c.seats - c.filled;
              const pct   = Math.round((c.filled / c.seats) * 100);
              const full  = avail === 0;
              return (
                <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 14, padding: '16px 18px', background: full ? '#f9fafb' : '#fffbeb' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
                    <span style={{ fontSize: 30 }}>{c.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 800, fontSize: 14, color: '#1e3a5f' }}>{c.title}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{c.subtitle} · {c.duration}</div>
                      <div style={{ fontSize: 11, color: '#92400e', marginTop: 2, fontWeight: 600 }}>🕐 {c.timing}</div>
                      {c.venue && <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>📍 {c.venue}</div>}
                      {c.nextBatch && <div style={{ fontSize: 10, color: '#059669', marginTop: 2, fontWeight: 600 }}>📆 Next Batch: {c.nextBatch}</div>}
                      {c.fee > 0 && <div style={{ fontSize: 12, color: '#1e3a5f', marginTop: 3, fontWeight: 800 }}>💰 ₹{c.fee.toLocaleString('en-IN')}</div>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                    {c.highlights.map(function(h) {
                      return <span key={h} style={{ background: '#fef3c7', color: '#92400e', fontSize: 10, fontWeight: 600, borderRadius: 6, padding: '2px 8px' }}>{h}</span>;
                    })}
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#64748b', marginBottom: 4 }}>
                      <span>{full ? '🔴 Fully Booked' : `🟢 ${avail} seats available`}</span>
                      <span>{c.filled}/{c.seats} enrolled</span>
                    </div>
                    <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: pct + '%', background: full ? '#ef4444' : '#C9A84C', borderRadius: 3, transition: 'width 0.4s' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      disabled={full}
                      onClick={function() { if (!full) setBookingCourse(c); }}
                      style={{ flex: 2, padding: '9px 0', background: full ? '#e5e7eb' : 'linear-gradient(135deg,#C9A84C,#92400e)', color: full ? '#9ca3af' : '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: full ? 'not-allowed' : 'pointer' }}>
                      {full ? 'Fully Booked' : '📋 Book a Seat Now'}
                    </button>
                    <a href="https://medicalyoga.in/courses" target="_blank" rel="noopener noreferrer"
                      style={{ flex: 1, padding: '9px 0', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 600, fontSize: 11, textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      Read More ↗
                    </a>
                  </div>
                </div>
              );
            })}
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <a href="https://medicalyoga.in/courses" target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 13, color: '#C9A84C', fontWeight: 700, textDecoration: 'none' }}>
                View all courses on medicalyoga.in ↗
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Course billing modal */}
      {bookingCourse && (
        <CourseBookingModal
          course={bookingCourse}
          profile={profile}
          onClose={function() { setBookingCourse(null); }}
        />
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   COURSE BOOKING MODAL
   Shows course details pre-filled with patient info + payment
═══════════════════════════════════════════════════════════ */
function CourseBookingModal({ course, profile, onClose }) {
  const [payMode, setPayMode]   = React.useState('');
  const [orgData, setOrgData]   = React.useState(null);
  const [booked, setBooked]     = React.useState(false);
  const [qrRef, setQrRef]       = React.useState(function() { return String(Date.now()); });
  const [qrTimer, setQrTimer]   = React.useState(60);
  const [loading, setLoading]   = React.useState(false);

  React.useEffect(function() {
    api.get(process.env.REACT_APP_URL + '/orgs')
      .then(function(r) {
        var orgs = r.data || [];
        var match = orgs.find(function(o) { return o.status === 'active'; }) || orgs[0];
        if (match) setOrgData(match);
      }).catch(function() {});
  }, []);

  React.useEffect(function() {
    if (payMode !== 'online' || booked) return;
    var t = setInterval(function() {
      setQrTimer(function(v) {
        if (v <= 1) { setQrRef(String(Date.now())); return 60; }
        return v - 1;
      });
    }, 1000);
    return function() { clearInterval(t); };
  }, [payMode, booked]);

  var upiVpa  = orgData ? orgData.upid : '';
  var baseAmt = course.fee || 0;
  var today   = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  function upiLink(vpa, name, amount, ref) {
    return 'upi://pay?pa=' + encodeURIComponent(vpa) + '&pn=' + encodeURIComponent(name) + '&am=' + amount + '&tn=' + encodeURIComponent('Course ' + ref) + '&cu=INR';
  }
  function qrImageUrl(data) {
    return 'https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=' + encodeURIComponent(data) + '&margin=10';
  }

  function generateInvoice() {
    return [
      '━━━━━━━━━━━━━━━━━━━━',
      '*🏥 ' + (orgData ? orgData.name : 'Medical Yoga Therapy Centre') + '*',
      orgData && orgData.address ? orgData.address : null,
      orgData && orgData.phone1 ? '📞 ' + orgData.phone1 : null,
      '*COURSE ENROLLMENT RECEIPT*',
      '━━━━━━━━━━━━━━━━━━━━',
      'Date: ' + today,
      '',
      '*Student Details*',
      'Name: ' + (profile ? profile.name : 'Patient'),
      profile && profile.patientCard ? 'Card: ' + profile.patientCard : null,
      profile && profile.number ? 'Phone: ' + profile.number : null,
      '',
      '*Course Details*',
      'Course: ' + course.title,
      'Level: ' + course.subtitle,
      'Duration: ' + course.duration,
      'Batch: ' + (course.nextBatch || 'Next Available'),
      'Timing: ' + course.timing,
      course.venue ? 'Venue: ' + course.venue : null,
      '',
      '*Fees*',
      'Course Fee: ₹' + baseAmt.toLocaleString('en-IN'),
      '',
      'Payment: ' + (payMode === 'cash' ? 'Cash at Centre' : 'Online UPI'),
      'Status: ' + (payMode === 'online' ? '⏳ Pending Verification' : '✅ Confirmed'),
      '━━━━━━━━━━━━━━━━━━━━',
      'Thank you for enrolling! 🙏',
      orgData && orgData.phone1 ? 'Queries: ' + orgData.phone1 : null,
    ].filter(function(l) { return l !== null; }).join('\n');
  }

  function shareOnWhatsApp() {
    try {
    var text = generateInvoice();
    var phone = profile && profile.number ? profile.number.replace(/\D/g, '') : '';
    if (!phone) { showSdToast('No phone number found for this patient.', 'error'); return; }
    var to    = phone.length === 10 ? '91' + phone : phone;
    window.open('https://wa.me/' + to + '?text=' + encodeURIComponent(text), '_blank', 'noopener,noreferrer');
    } catch(e) { console.warn('WhatsApp share failed:', e); }
  }

  function handleConfirm() {
    if (!payMode || loading) return;
    setLoading(true);
    // Simulate a brief wait then mark as booked
    setTimeout(function() { setLoading(false); setBooked(true); }, 600);
  }

  var ov = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', zIndex: 9100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 };
  var bx = { background: '#fff', borderRadius: 20, width: '100%', maxWidth: 500, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.4)' };
  var card = function(active) { return { border: '2px solid ' + (active ? '#C9A84C' : '#d1d5db'), borderRadius: 12, padding: '12px 16px', cursor: 'pointer', background: active ? '#fffbeb' : '#fff', transition: 'all 0.15s' }; };

  return (
    <div style={ov}>
      <div style={bx}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg,#C9A84C,#92400e)', padding: '18px 22px', borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 900, fontSize: 15 }}>📋 Course Booking</div>
            <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11, marginTop: 2 }}>{course.title}</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>✕ Close</button>
        </div>

        <div style={{ padding: '20px 22px' }}>
          {!booked ? (
            <>
              {/* Pre-filled student info */}
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 12 }}>
                <div style={{ fontWeight: 700, color: '#1e3a5f', marginBottom: 6 }}>Student Details</div>
                <div><span style={{ color: '#64748b' }}>Name:</span> <strong>{profile ? profile.name : '—'}</strong></div>
                <div><span style={{ color: '#64748b' }}>Card:</span> {profile ? profile.patientCard : '—'}</div>
                <div><span style={{ color: '#64748b' }}>Phone:</span> {profile ? profile.number : '—'}</div>
              </div>

              {/* Course details */}
              <div style={{ background: '#fffbeb', borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 12 }}>
                <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 6 }}>Course Details</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', lineHeight: 1.7 }}>
                  <div><span style={{ color: '#64748b' }}>Course:</span></div>
                  <div><strong>{course.title}</strong></div>
                  <div><span style={{ color: '#64748b' }}>Level:</span></div>
                  <div>{course.subtitle}</div>
                  <div><span style={{ color: '#64748b' }}>Duration:</span></div>
                  <div>{course.duration}</div>
                  <div><span style={{ color: '#64748b' }}>Next Batch:</span></div>
                  <div><strong style={{ color: '#059669' }}>{course.nextBatch || 'TBD'}</strong></div>
                  <div><span style={{ color: '#64748b' }}>Timings:</span></div>
                  <div>{course.timing}</div>
                  {course.venue && <>
                    <div><span style={{ color: '#64748b' }}>Venue:</span></div>
                    <div style={{ fontSize: 11 }}>{course.venue}</div>
                  </>}
                </div>
                <div style={{ marginTop: 10, fontWeight: 800, fontSize: 15, color: '#92400e' }}>
                  Course Fee: ₹{baseAmt.toLocaleString('en-IN')}
                </div>
              </div>

              {/* Payment method */}
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1e3a5f', marginBottom: 10 }}>Select Payment</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                {[
                  { id: 'cash',   icon: '💵', title: 'Cash at Centre', sub: 'Pay on arrival or at registration desk.' },
                  { id: 'online', icon: '📱', title: 'Online UPI — ₹' + baseAmt.toLocaleString('en-IN'), sub: 'Scan QR & pay. Seat confirmed after verification.' },
                ].map(function(p) {
                  return (
                    <div key={p.id} onClick={function() { setPayMode(p.id); }}
                      style={Object.assign({}, card(payMode === p.id), { display: 'flex', alignItems: 'center', gap: 14 })}>
                      <span style={{ fontSize: 26 }}>{p.icon}</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e3a5f' }}>{p.title}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{p.sub}</div>
                      </div>
                      {payMode === p.id && <span style={{ marginLeft: 'auto', color: '#C9A84C', fontSize: 20 }}>✓</span>}
                    </div>
                  );
                })}
              </div>

              {/* QR for online */}
              {payMode === 'online' && (
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  {upiVpa ? (
                    <>
                      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                        Scan to pay ₹{baseAmt} — refreshes in <strong>{qrTimer}s</strong>
                      </div>
                      <img src={qrImageUrl(upiLink(upiVpa, orgData ? orgData.name : 'MedicalYog', baseAmt, qrRef))}
                        alt="UPI QR" width={190} height={190}
                        style={{ borderRadius: 12, border: '2px solid #e2e8f0' }} />
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 6, fontWeight: 600 }}>UPI: {upiVpa}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>After paying, click "Done — I've Paid"</div>
                    </>
                  ) : (
                    <div style={{ background: '#fef3c7', borderRadius: 10, padding: '12px', fontSize: 12, color: '#92400e' }}>
                      ⚠️ UPI not configured. Please pay cash at centre.
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={onClose} style={{ flex: 1, padding: '11px 0', background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                  ‹ Back
                </button>
                <button disabled={!payMode || loading} onClick={handleConfirm}
                  style={{ flex: 2, padding: '11px 0', background: payMode && !loading ? (payMode === 'cash' ? '#059669' : '#C9A84C') : '#e2e8f0', color: payMode && !loading ? '#fff' : '#9ca3af', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: payMode && !loading ? 'pointer' : 'not-allowed' }}>
                  {loading ? 'Processing…' : payMode === 'cash' ? '✓ Confirm Enrollment' : payMode === 'online' ? '✓ Done — I\'ve Paid' : 'Select Payment'}
                </button>
              </div>
            </>
          ) : (
            /* ── Confirmed ─────────────────────────────── */
            <div style={{ textAlign: 'center', padding: '10px 0' }}>
              {payMode === 'online' ? (
                <>
                  <div style={{ fontSize: 44, marginBottom: 10 }}>⏳</div>
                  <div style={{ fontWeight: 900, fontSize: 17, color: '#92400e', marginBottom: 6 }}>Payment Under Review</div>
                  <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6, marginBottom: 16 }}>
                    We received your enrollment.<br />Seat confirmed after payment verification.
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 44, marginBottom: 10 }}>✅</div>
                  <div style={{ fontWeight: 900, fontSize: 17, color: '#059669', marginBottom: 6 }}>Enrollment Confirmed!</div>
                </>
              )}
              {/* Receipt */}
              <div style={{ background: payMode === 'online' ? '#fef3c7' : '#f0fdf4', borderRadius: 12, padding: '14px 16px', fontSize: 12, textAlign: 'left', lineHeight: 1.8, marginBottom: 16 }}>
                <div style={{ fontWeight: 800, fontSize: 13, color: '#1e3a5f', marginBottom: 6 }}>🎓 {course.title}</div>
                <div>Duration: {course.duration} · Starts: {course.nextBatch || 'TBD'}</div>
                <div>Timing: {course.timing}</div>
                {course.venue && <div style={{ fontSize: 11 }}>📍 {course.venue}</div>}
                <div style={{ fontWeight: 700, marginTop: 6 }}>💰 Fee: ₹{baseAmt.toLocaleString('en-IN')} · {payMode === 'cash' ? 'Cash' : 'UPI'}</div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={shareOnWhatsApp}
                  style={{ flex: 1, padding: '12px 0', background: '#25D366', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
                  📲 Share Receipt
                </button>
                <button onClick={onClose}
                  style={{ flex: 1, padding: '12px 0', background: 'linear-gradient(135deg,#C9A84C,#92400e)', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
                  Done ✓
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   CONTACT US MODAL — Chatbot-style query picker
   Patient selects a message template → composes query → sends via WhatsApp
   Also sends in-app notification to admin/supervisor/assigned therapist
═══════════════════════════════════════════════════════════ */
function ContactUsModal({ onClose, profile }) {
  var URL = process.env.REACT_APP_URL;
  var CENTRE_PHONE = '9891243444';
  var WA_LINK = 'https://wa.me/91' + CENTRE_PHONE;

  var [chatStep, setChatStep] = React.useState('welcome'); // welcome | templates | custom | sent
  var [selectedTemplate, setSelectedTemplate] = React.useState(null);
  var [customMsg, setCustomMsg] = React.useState('');
  var [sending, setSending] = React.useState(false);

  var templates = [
    { id: 'reschedule', icon: '📅', label: 'Reschedule my appointment', msg: 'I would like to reschedule my upcoming appointment. Please let me know available slots.' },
    { id: 'book-other', icon: '👥', label: 'Book appointment for someone else', msg: 'I would like to book an appointment for someone else. Please assist.' },
    { id: 'coupons', icon: '🎟️', label: 'Coupons available on my login', msg: 'I would like to know about coupons / offers available on my account.' },
    { id: 'write-doctor', icon: '✉️', label: 'Write to Dr Deepak Sachdeva directly', msg: 'I would like to write directly to Dr Deepak Sachdeva regarding my treatment.' },
    { id: 'feedback', icon: '⭐', label: 'Share feedback', msg: 'I would like to share feedback about my experience.' },
    { id: 'anon-feedback', icon: '🕵️', label: 'Give anonymous feedback', msg: '' },
    { id: 'other', icon: '💬', label: 'Something else', msg: '' },
  ];

  function buildFullMessage(queryText, anonymous) {
    var lines;
    if (anonymous) {
      lines = [
        'ANONYMOUS FEEDBACK',
        '___________',
        '',
        'Feedback:',
        queryText,
        '',
        'Sent from Medical Yoga App',
      ];
    } else {
      lines = [
        'PATIENT QUERY',
        '___________',
        '',
        'From: ' + (profile ? profile.name : 'Patient'),
        'Card: ' + (profile ? profile.patientCard : ''),
        'Phone: ' + (profile ? profile.number : ''),
        '',
        'Query:',
        queryText,
        '',
        'Sent from Medical Yoga App',
      ];
    }
    return lines.join('\n');
  }

  var isAnonymous = selectedTemplate && selectedTemplate.id === 'anon-feedback';

  function handleSend(queryText) {
    if (!queryText || !queryText.trim()) return;
    setSending(true);

    // 1. Send in-app notification to staff (therapist + admin)
    var notifBody = {
      type: isAnonymous ? 'ANONYMOUS_FEEDBACK' : 'PATIENT_QUERY',
      title: isAnonymous ? 'Anonymous Feedback' : ('Patient Query: ' + (profile ? profile.name : 'Unknown')),
      message: queryText,
      patientCard: isAnonymous ? '' : (profile ? profile.patientCard : ''),
      patientName: isAnonymous ? 'Anonymous' : (profile ? profile.name : ''),
    };
    // Always notify admin
    api.post(URL + '/notifications', Object.assign({}, notifBody, { userId: 'admin' })).catch(function() {});
    // Notify assigned therapist (unless anonymous)
    if (!isAnonymous) {
      var therapist = profile ? profile.assignedTherapist || '' : '';
      if (therapist && therapist !== 'admin') {
        api.post(URL + '/notifications', Object.assign({}, notifBody, { userId: therapist })).catch(function() {});
      }
    }

    // 2. Open WhatsApp with the composed message
    var fullMsg = buildFullMessage(queryText, isAnonymous);
    window.open(WA_LINK + '?text=' + encodeURIComponent(fullMsg), '_blank', 'noopener,noreferrer');

    setSending(false);
    setChatStep('sent');
  }

  var chatBubbleLeft = function(content, key) {
    return (
      <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#2563eb,#059669)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
          fontWeight: 800, fontSize: 13, flexShrink: 0,
        }}>M</div>
        <div style={{
          background: '#f0f4ff', borderRadius: '4px 16px 16px 16px', padding: '10px 14px',
          maxWidth: '80%', fontSize: 13, color: '#1e3a5f', lineHeight: 1.5,
        }}>{content}</div>
      </div>
    );
  };

  var chatBubbleRight = function(content, key) {
    return (
      <div key={key} style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <div style={{
          background: '#2563eb', borderRadius: '16px 4px 16px 16px', padding: '10px 14px',
          maxWidth: '80%', fontSize: 13, color: '#fff', lineHeight: 1.5,
        }}>{content}</div>
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 2000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '0 0 0 0' }}>
      <div style={{
        background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 540,
        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 -8px 40px rgba(0,0,0,0.25)',
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', padding: '16px 20px',
          borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
            <div>
              <div style={{ color: '#fff', fontWeight: 800, fontSize: 15 }}>Contact Us</div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>Medical Yoga Centre</div>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)',
            color: '#fff', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 14, fontWeight: 700,
          }}>{'\u2715'}</button>
        </div>

        {/* Chat body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 14px' }}>

          {/* Welcome step */}
          {chatStep === 'welcome' && (
            <div>
              {chatBubbleLeft(<span>
                Namaste {profile ? profile.name : ''} {'🙏'}<br/><br/>
                Welcome to Medical Yoga Centre! How can we help you today?
              </span>, 'welcome')}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12, paddingLeft: 40 }}>
                <button onClick={function() { setChatStep('templates'); }}
                  style={{
                    padding: '11px 16px', background: '#fff', border: '1.5px solid #2563eb',
                    borderRadius: '4px 14px 14px 14px', cursor: 'pointer', textAlign: 'left',
                    fontSize: 13, fontWeight: 600, color: '#2563eb',
                  }}>
                  I have a query {'💬'}
                </button>
                <button onClick={function() {
                  window.open('tel:+91' + CENTRE_PHONE, '_self');
                }}
                  style={{
                    padding: '11px 16px', background: '#fff', border: '1.5px solid #059669',
                    borderRadius: '4px 14px 14px 14px', cursor: 'pointer', textAlign: 'left',
                    fontSize: 13, fontWeight: 600, color: '#059669',
                  }}>
                  Call us directly {'📞'}
                </button>
                <button onClick={function() {
                  window.open(WA_LINK, '_blank', 'noopener,noreferrer');
                }}
                  style={{
                    padding: '11px 16px', background: '#fff', border: '1.5px solid #25D366',
                    borderRadius: '4px 14px 14px 14px', cursor: 'pointer', textAlign: 'left',
                    fontSize: 13, fontWeight: 600, color: '#25D366',
                  }}>
                  Chat on WhatsApp {'💚'}
                </button>
              </div>
            </div>
          )}

          {/* Template selection */}
          {chatStep === 'templates' && (
            <div>
              {chatBubbleLeft('Please select what you need help with:', 'q1')}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12, paddingLeft: 40 }}>
                {templates.map(function(t) {
                  return (
                    <button key={t.id} onClick={function() {
                      setSelectedTemplate(t);
                      if (t.id === 'other') {
                        setChatStep('custom');
                      } else {
                        setChatStep('custom');
                        setCustomMsg(t.msg);
                      }
                    }}
                      style={{
                        padding: '10px 14px', background: '#fff', border: '1.5px solid #e2e8f0',
                        borderRadius: '4px 14px 14px 14px', cursor: 'pointer', textAlign: 'left',
                        fontSize: 13, color: '#1e3a5f', display: 'flex', alignItems: 'center', gap: 10,
                      }}>
                      <span style={{ fontSize: 18 }}>{t.icon}</span>
                      <span style={{ fontWeight: 600 }}>{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Custom message / edit template */}
          {chatStep === 'custom' && (
            <div>
              {chatBubbleLeft('Please select what you need help with:', 'q1b')}
              {selectedTemplate && selectedTemplate.id !== 'other' && (
                chatBubbleRight(selectedTemplate.label, 'sel')
              )}
              {chatBubbleLeft(
                selectedTemplate && selectedTemplate.id !== 'other'
                  ? 'You can edit the message below or add more details, then tap Send:'
                  : 'Type your query below and tap Send:',
                'q2'
              )}
              <div style={{ paddingLeft: 40, marginTop: 8 }}>
                <textarea
                  value={customMsg}
                  onChange={function(e) { setCustomMsg(e.target.value); }}
                  placeholder="Type your message here..."
                  rows={4}
                  style={{
                    width: '100%', padding: '10px 12px', border: '1.5px solid #d1d5db',
                    borderRadius: 12, fontSize: 13, resize: 'vertical', boxSizing: 'border-box',
                    fontFamily: 'inherit', lineHeight: 1.5,
                  }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button onClick={function() { setChatStep('templates'); setCustomMsg(''); setSelectedTemplate(null); }}
                    style={{
                      flex: 1, padding: '10px 0', background: '#f1f5f9', color: '#64748b',
                      border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer',
                    }}>Back</button>
                  <button
                    disabled={sending || !customMsg.trim()}
                    onClick={function() { handleSend(customMsg.trim()); }}
                    style={{
                      flex: 2, padding: '10px 0',
                      background: customMsg.trim() && !sending ? '#25D366' : '#e2e8f0',
                      color: customMsg.trim() && !sending ? '#fff' : '#9ca3af',
                      border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13,
                      cursor: customMsg.trim() && !sending ? 'pointer' : 'not-allowed',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                    {sending ? 'Sending...' : 'Send via WhatsApp'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Sent confirmation */}
          {chatStep === 'sent' && (
            <div>
              {chatBubbleLeft('Please select what you need help with:', 'q1c')}
              {selectedTemplate && chatBubbleRight(selectedTemplate.label, 'sel2')}
              {customMsg && chatBubbleRight(customMsg.length > 60 ? customMsg.slice(0, 60) + '...' : customMsg, 'msg')}
              {chatBubbleLeft(<span>
                {'✅'} Thank you for your query!<br/><br/>
                Your message has been sent. Our team will get back to you shortly.<br/><br/>
                You can also reach us at:<br/>
                {'📞'} <strong>011-45525456</strong><br/>
                {'📱'} <strong>+91 9891 24 34 44</strong>
              </span>, 'thanks')}
              <div style={{ paddingLeft: 40, marginTop: 12 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={function() { setChatStep('welcome'); setCustomMsg(''); setSelectedTemplate(null); }}
                    style={{
                      flex: 1, padding: '10px 0', background: '#2563eb', color: '#fff',
                      border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    }}>New Query</button>
                  <button onClick={onClose}
                    style={{
                      flex: 1, padding: '10px 0', background: '#059669', color: '#fff',
                      border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    }}>Done</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
